import { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Box, Typography, IconButton, Tooltip, CircularProgress, Skeleton } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import TuneIcon from "@mui/icons-material/Tune";
import GridViewIcon from "@mui/icons-material/GridViewOutlined";
import NewspaperIcon from "@mui/icons-material/NewspaperOutlined";
import StarIcon from "@mui/icons-material/Star";
import BatchPredictionIcon from "@mui/icons-material/BatchPredictionOutlined";
import AnalyticsIcon from "@mui/icons-material/AnalyticsOutlined";
import WarehouseIcon from "@mui/icons-material/WarehouseOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShippingOutlined";
import FactoryIcon from "@mui/icons-material/FactoryOutlined";
import DashboardIcon from "@mui/icons-material/DashboardOutlined";
import AddIcon from "@mui/icons-material/Add";
import ChecklistIcon from "@mui/icons-material/ChecklistOutlined";
import PointOfSaleIcon from "@mui/icons-material/PointOfSaleOutlined";
import NewReleasesIcon from "@mui/icons-material/NewReleasesOutlined";
import ShieldIcon from "@mui/icons-material/ShieldOutlined";
import WavingHandIcon from "@mui/icons-material/WavingHandOutlined";
import FactCheckIcon from "@mui/icons-material/FactCheckOutlined";
import NotificationsIcon from "@mui/icons-material/NotificationsOutlined";
import AccountTreeIcon from "@mui/icons-material/AccountTreeOutlined";
import EventNoteIcon from "@mui/icons-material/EventNoteOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesomeOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

import AppCard, { CARD_HEADER_SX } from "@plannel/components/ui/AppCard";
import { CARD_HOVER_SX, CARD_HOVER_TRANSITION, CARD_TITLE_CLASS } from "@plannel/components/dashboard/DashboardCard";
import { KpiCard } from "@plannel/components/dashboard/KpiCard";
import HomeCardModal from "@plannel/components/dashboard/HomeCardModal";
import DashboardPinModal from "@plannel/components/dashboard/DashboardPinModal";
import useDashboardP13n from "@plannel/hooks/useDashboardP13n";
import { PINNED_PAGE_CD } from "@plannel/components/dashboard/PinDashboardButton";
import p13nService from "@plannel/services/system/p13n-service";
import planVersionService from "@plannel/services/data/plan-version-service";
import notificationService from "@plannel/services/system/notification-service";
import meetingAgendaService from "@plannel/services/system/meeting-agenda-service";
import { AGENDA_SCOPE, AGENDA_SCOPE_EVENT } from "@plannel/pages/collaboration/agendaScope";
import planConfigService from "@plannel/services/data/plan-config-service";
import salesTxnService from "@plannel/services/data/sales-txn-service";
import customerItemService from "@plannel/services/data/customer-item-service";
import locationItemService from "@plannel/services/data/location-item-service";
import dpProcessMgmtService from "@plannel/services/dp/dp-process-mgmt-service";
import homeDwService from "@plannel/services/dashboard/home-dw-service";
import { makeFmtMoney, fmtQty } from "@plannel/components/dashboard/dashboardFormat";
import itemService from "@plannel/services/data/item-service";
import dateUtils from "@plannel/utils/date-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import { openWorkflowPanel } from "@plannel/redux/modules/WorkflowPanelState";
import tabMenuList from "@plannel/pages/TabMenuList";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import ColorUtil from "@plannel/pages/utils/color-util";
import debug from "@plannel/utils/debug-util";
import { HomeSignalProvider, useReportHomeSignal, useHomeSignals } from "@plannel/pages/home/HomeSignalContext";
import useSharedDwRequest from "@plannel/pages/home/useSharedDwRequest";
import HomeAiCard from "@plannel/pages/home/HomeAiCard";
import HomeBroadsheet from "@plannel/pages/home/HomeBroadsheet";
import companyService from "@plannel/services/data/company-service";
import { FS } from "@plannel/utils/font-util";
import homeImage from "@plannel/assets/home.png";

/*
 * 홈(랜딩) — "Planning Command Center" 벤토 그리드 (모듈 권한 인지형).
 *
 * 앱 셸(사이드바 + 상단 Navbar)은 App 레벨에서 그려지므로 여기서는 콘텐츠 영역만 채운다.
 *
 * ── 권한 인지 (핵심) ─────────────────────────────────────────────
 * 카드는 하드코딩 배열이 아니라 CARD_CATALOG(레지스트리)로 정의하고, 각 카드에 module 을 태그한다.
 * 홈은 사용자 roles(localStorage "user".roles, Sidebar 와 동일 소스)로 카탈로그를 먼저 거른다.
 *   - module === "core"  → 항상 표시(모듈 무관: 인사말·알림·계획 준비 현황·내 할 일·고정 대시보드 6종)
 *   - 그 외             → roles 에 해당 ROLE_APP_* 이 있어야 표시
 *     계획 5모듈(BF/DP/IP/RP/MP) + **AI(ROLE_APP_AI)**. AI 는 계획 모듈이 아니라
 *     MODULES(파이프라인·버전 칩) 에는 넣지 않고 카드 게이팅·색에만 참여한다.
 * 권한 없는 모듈 카드는 애초에 목록에 안 들어오므로 빈 카드도, 불필요한 조회도 없다.
 * core 카드가 항상 있어 최소 권한 사용자도 홈이 비지 않는다.
 *
 * 커스터마이징(표시/순서 개인화, z_p13n)은 다음 단계 — useDashboardP13n + DashboardLayoutModal 재사용 예정.
 *
 * 스타일: 카드 셸=AppCard, KPI 타일=components/dashboard/KpiCard, 색=MUI 시맨틱 토큰(새 hex 금지).
 * 각 데이터 카드는 "동기화 시점 스냅샷"(as-of 칩)을 스스로 띄운다.
 *
 * i18n: home* 키는 6개 언어 translation.*.json 에 모두 있다(menu / plannelAgent 섹션).
 *       파일 안에 폴백 표는 없다 — 문구를 고치려면 언어팩을 고친다.
 */

const ROLE = { DP: "ROLE_APP_DP", IP: "ROLE_APP_IP", RP: "ROLE_APP_RP", MP: "ROLE_APP_MP", BF: "ROLE_APP_BF", AI: "ROLE_APP_AI" };

// 모듈별 색 — 순번표는 ColorUtil 하나뿐이다(Meeting Agenda 등 다른 화면도 같은 것을 쓴다).
//   실제 색은 렌더 시점에 뽑으므로 사용자가 고른 팔레트를 따라가고, 모듈이 아닌 카드엔 null 이 온다.
const moduleHex = (moduleKey) => ColorUtil.moduleColor(moduleKey);

// 모듈 아이콘(MUI) · 이름 키 — 카드/칩에 "모듈색 아이콘 배지"로 붙여 소속 모듈을 표시(배경 틴트 대신).
// 모듈 아이콘 = Plannel 사이드바 lv1 메뉴 아이콘 그대로(일관성): DP=Analytics, IP=Warehouse, RP=LocalShipping, MP=Factory.
//   BF 는 전용 lv1 이 없다. 라인차트 계열(InsightsRounded/AutoGraph)은 BI 와 닮아서 피하고, "예측(prediction)" 의미의 BatchPrediction 을 쓴다.
const MODULE_ICON = { BF: BatchPredictionIcon, DP: AnalyticsIcon, IP: WarehouseIcon, RP: LocalShippingIcon, MP: FactoryIcon, AI: AutoAwesomeIcon };
// 파이프라인 단계 아이콘 = 모듈 아이콘 + 판매실적(모듈 아님, 실적 데이터라 PointOfSale·중립색).
const STAGE_ICON = { salesActual: PointOfSaleIcon, ...MODULE_ICON };
// 단계별 상태 설명 — 배지(정상/확인 N건 등)가 "무엇을 세는지" 알려주는 고정 문구(실데이터와 무관).
const STAGE_DESC_KEY = { salesActual: "homeStageDescSales", BF: "homeStageDescBf", DP: "homeStageDescDp", IP: "homeStageDescIp", RP: "homeStageDescRp", MP: "homeStageDescMp" };
// 모듈명 번역 키 — 색 순번표와 같은 곳(ColorUtil)에 둔다. 둘이 갈라지면 한쪽에만 모듈이 늘어난다.
const MODULE_NAME_KEY = ColorUtil.MODULE_NAME_KEY;

// 카드 인식용 아이콘 — 카드 헤더/설정 목록 공용. core 카드는 전용 아이콘, 모듈 카드는 해당 모듈 아이콘.
const CARD_ICON = { greeting: WavingHandIcon, pipeline: FactCheckIcon, notifications: NotificationsIcon, approvals: AccountTreeIcon, myTasks: ChecklistIcon, pinned: DashboardIcon, aiPrompt: AutoAwesomeIcon, npi: NewReleasesIcon, safetyStock: ShieldIcon, meetingAgenda: EventNoteIcon };
const cardIconOf = (card) => CARD_ICON[card.key] ?? MODULE_ICON[card.module] ?? null;

/*
  core 카드 배지색 — 모듈색(0~4) 다음 색 **하나**를 다같이 쓴다(default 팔레트에선 보라).
  카드마다 다른 색을 주면 색이 곧 소속인 것처럼 보이는데(모듈 카드는 실제로 그렇다)
  core 카드끼리는 나눌 소속이 없다 — 회색만 벗으면 충분하다.
  모듈색과 같은 ColorUtil.colorSet 이라 사용자가 고른 팔레트를 따라간다.
  인사말도 여기 포함이다 — 예전엔 warning(amber) 하나만 따로 썼는데, 색이 곧 소속이라
  core 안에서 인사말만 다른 색일 이유가 없다(ctx.greetingHex 도 같이 없앴다).

  ★ 규칙: **같은 role 은 같은 아이콘색.** 색이 곧 권한 소속이라 role 하나에 색 하나다.
    role 을 새로 붙이는 카드는 core 색을 쓰면 안 되고 ColorUtil.moduleColor 의 순번표에서 제 번호를 받아야 한다
    (AI 카드가 ROLE_APP_AI 로 갈라져 나갈 때 coreHex → moduleHex("AI") 로 바꾼 이유).
*/
/*
  홈 배치 방식.
  ⚠️ 화면 문구는 "격자 / 신문"(Grid / Newspaper) 이지만 **저장값은 bento/broadsheet 그대로**다.
    저장값은 z_p13n 에 이미 들어간 사용자 설정이라 바꾸면 그 값들이 기본으로 되돌아간다.
    라벨과 저장값이 다른 건 의도다 — 라벨은 언제든 다시 바꿀 수 있어야 하고,
    저장값은 한 번 정하면 바꾸는 데 마이그레이션이 필요하다.
*/
const LAYOUT_MODE_OPTIONS = [
  { value: "bento", labelKey: "homeLayoutGrid" },
  { value: "broadsheet", labelKey: "homeLayoutNewspaper" },
];

/*
  카드 설정 목록의 **고정 구역**. 신문 지면에서 자리가 데이터와 무관하게 정해지는 것만 묶는다.
  ⚠️ Main / BRIEFS / OTHER FIGURES 로는 못 묶는다 — 그건 신호에 따라 매일 달라져서,
    목록을 열 때마다 카드가 그룹 사이를 옮겨 다니고 그룹 안에서 순서를 바꿔도 아무 일도
    안 일어난다(자리는 rankSignals 가 정한다). 동작하지 않는 컨트롤이 되므로 만들지 않는다.
  ⚠️ 여기 순서가 곧 저장되는 order 의 큰 틀이다 — 모달이 구역 순으로 재조립한다.
*/
const CARD_REGIONS = [
  { id: "masthead", labelKey: "homeRegionMasthead", keys: ["greeting"] },
  { id: "ai", labelKey: "homeAiPrompt", keys: ["aiPrompt"] },
  { id: "desk", labelKey: "homeDeskRail", keys: ["myTasks", "pinned", "meetingAgenda", "notifications", "pipeline"] },
  // 나머지 전부(모듈 카드). 신호가 있으면 기사, 없으면 지표 띠로 간다.
  { id: "sheet", labelKey: "homeRegionSheet", keys: null },
];
const regionOf = (key) =>
  (CARD_REGIONS.find((r) => r.keys?.includes(key)) ?? CARD_REGIONS[CARD_REGIONS.length - 1]).id;

const CORE_TINT_INDEX = 5;
const coreHex = () => ColorUtil.colorSet(CORE_TINT_INDEX);

// 아이콘 색을 못 받은 배지용 최후 폴백(중립 회색).
const CORE_ICON_HEX = "#64748b";

/*
  저장된 layout(z_p13n)이 도착하기 전에 보여 줄 자리표시자.

  왜 필요한가: 배치가 저장값에 따라 갈리므로(벤토 격자 / 신문 지면) 값이 오기 전에는 아무것도
  그릴 수 없다. 그렇다고 빈 화면을 두면 응답이 늦을 때 홈이 통째로 비어 보인다(실제로 봤다).
  ⚠️ 어느 배치가 될지 모르므로 **중립적인 형태**로 둔다 — 벤토를 흉내 내면 신문으로 열릴 때
    스켈레톤과 실제 화면이 어긋나 그게 또 번쩍임으로 읽힌다. 그래서 큰 덩어리 + 균일한 격자다.
*/
const HomeSkeleton = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flexShrink: 0 }} aria-busy="true">
    <Skeleton variant="rounded" height={64} sx={{ borderRadius: 1.5 }} />
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0,1fr))", lg: "repeat(4, minmax(0,1fr))" }, gap: 1.5 }}>
      {Array.from({ length: 8 }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Skeleton key={i} variant="rounded" height={150} sx={{ borderRadius: 1.5 }} />
      ))}
    </Box>
  </Box>
);

/*
  카드를 전부 끈 홈 — 벤토 홈 이전의 랜딩 화면(d0e5af0a8a 에서 교체됨)을 그대로 되살린다.
  이미지·스타일 모두 당시 것이다: assets/home.png + App.scss 의 .home-area/.home-image.
*/
const HomeEmpty = () => (
  <Box className="home-area" sx={{ flex: 1, minHeight: 0 }}>
    <Box className="home-image" title="home" sx={{ backgroundImage: `url(${homeImage})` }} />
  </Box>
);

// 흰 카드 위 아이콘 타일(옅은 틴트 배경 + 아이콘). 카드 헤더 아이콘은 KPI 타일이든 전용 카드든
//   전부 이 배지를 거친다 — 맨몸 아이콘과 섞이면 같은 화면에서 아이콘 크기·무게가 제각각으로 보인다.
/*
  로딩 표시는 300ms 지나서야 켠다 — 카드마다 호출이 따로 끝나는데 빠른 응답까지 링을 그리면
  랜딩이 깜빡임 범벅이 된다. 그 안에 끝나면 사용자는 로딩을 아예 못 본다(그게 맞다).
*/
const useDelayedFlag = (active, ms = 300) => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!active) {
      setOn(false);
      return undefined;
    }
    const timer = setTimeout(() => setOn(true), ms);
    return () => clearTimeout(timer);
  }, [active, ms]);
  return on;
};

/*
  인사말의 '확인 필요 N곳' — 홈 카드들이 신고한 경고 수(HomeSignalContext).
  새 임계치를 만들지 않는다. 각 카드가 이미 계산해 배지로 띄운 판정을 그대로 세므로,
  화면에 경고가 보이는 카드 수와 항상 일치한다.

  ⚠️ 세는 단위는 '항목' 이 아니라 '카드' 다 — 신호의 count 는 단위가 제각각이고(mpCapacity 는
    건수가 아니라 부하율 %), count 가 아예 없는 신호도 있어 더할 수 없다. 그래서 문구도
    '오늘 확인할 항목 N건' 이 아니라 '확인 필요 N곳' 이다.
  ⚠️ info 는 세지 않는다 — '내 할 일'·'알림' 은 문제가 아니라 평상시 상태고, 그 카드들이
    이미 자기 숫자를 크게 띄우고 있다.
  ⚠️ 별도 컴포넌트인 이유: useHomeSignals() 는 신호가 도착할 때마다 재렌더된다. 인사말 본체에서
    부르면 카드 전체가, Home 본체에서 부르면 그리드 전체가 다시 그려진다(HomeSignalContext 주석).
*/
const GreetingAlertCount = ({ ttv }) => {
  const signals = useHomeSignals();
  const n = signals.filter((s) => s.severity === "error" || s.severity === "warn").length;
  if (n === 0) return null;
  // 라벨·숫자·단위를 이어붙이지 않고 한 문장으로 번역한다 — 어순이 언어마다 달라서
  // "확인 필요 3곳" 을 조각으로 맞추면 영어가 "needs review 3" 이 된다.
  return <>{` · ${ttv("homeNeedsCheckCount", { count: n })}`}</>;
};

const CardIconBadge = ({ Icon, hex, tooltip, size = 26, loading = false }) => {
  const spin = useDelayedFlag(loading);
  if (!Icon) return null;
  const c = hex || CORE_ICON_HEX;   // moduleHex 는 모듈 없는 카드에 null 을 준다(기본값 인자로는 안 걸림)
  /*
    로딩은 카드를 회색으로 죽이지 않고 배지 둘레의 링으로만 알린다.
      - 회색 dim 은 이미 '준비중 스텁'·'권한 없음' 이 쓰는 신호라 겹치면 안 된다.
      - 아이콘·제목은 계속 보여야 값이 오기 전에도 무슨 카드인지 읽힌다.
    링은 배지 밖으로 도는 오버레이라 배지 크기가 안 변하고, 따라서 레이아웃도 안 밀린다.
  */
  const badge = (
    <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0, display: "flex" }}>
      <Box sx={{ width: size, height: size, borderRadius: 1.25, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha(c, 0.14), color: c }}>
        <Icon sx={{ fontSize: Math.round(size * 0.62) }} />
      </Box>
      {spin && (
        <CircularProgress size={size + 6} thickness={3.2}
          sx={{ position: "absolute", top: -3, left: -3, color: alpha(c, 0.55), pointerEvents: "none" }} />
      )}
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip}>{badge}</Tooltip> : badge;
};

// 모듈 KPI 타일용 — 모듈 아이콘 + 모듈색 + 모듈명 Tooltip.
const ModuleIconBadge = ({ tt, moduleKey, size = 26, loading = false }) => {
  const hex = moduleHex(moduleKey);
  const Icon = MODULE_ICON[moduleKey];
  if (!Icon || !hex) return null;
  return <CardIconBadge Icon={Icon} hex={hex} tooltip={tt(MODULE_NAME_KEY[moduleKey])} size={size} loading={loading} />;
};

// 파이프라인/바로가기 표기용 모듈 메타(권한 순서대로)
const MODULES = [
  { key: "BF", role: ROLE.BF, nameKey: "homeModBf" },
  { key: "DP", role: ROLE.DP, nameKey: "homeModDp" },
  { key: "IP", role: ROLE.IP, nameKey: "homeModIp" },
  { key: "RP", role: ROLE.RP, nameKey: "homeModRp" },
  { key: "MP", role: ROLE.MP, nameKey: "homeModMp" },
];

// 기본 레이아웃의 카드 묶음 순서 — 같은 모듈 카드가 화면에서 붙어 보이도록 그룹 단위로 정렬한다.
//   카탈로그는 목적별로 읽기 좋게 두고, 순서만 여기서 정한다(새 카드가 어디에 정의되든 자동으로 제 그룹에 붙는다).
const CARD_GROUP_ORDER = ["core", ...MODULES.map((m) => m.key)];

// AI 카드는 권한만 따로일 뿐(ROLE_APP_AI) 화면에서는 core 와 같은 상단 자리에 둔다.
//   MODULES 에 넣으면 계획 파이프라인·버전 칩에까지 끼어들고, 그냥 두면 indexOf 가 -1 이라
//   "우연히" 맨 앞으로 간다. 어느 쪽도 의도가 아니라서 순서상 core 로 친다고 명시한다.
const cardGroupRank = (module) => CARD_GROUP_ORDER.indexOf(module === "AI" ? "core" : module);

// 인사말 버전 칩 클릭 → 모듈별 페이지로 버전 프리셋. viewName==reduxKey, 전체 버전 객체를 해당 키에 심음(id 매칭).
//   DP=DP Workbench, RP=RP Review, IP=Target Inventory Simulation Results, MP=MP Review.
const CHIP_DRILL = {
  DP: { viewName: "DP_WORKBENCH", key: "selectVersion" },
  RP: { viewName: "RP_REVIEW", key: "rpVersion" },
  IP: { viewName: "TARGET_INV_SIMULATION_RESULTS", key: "ipVersion" },
  MP: { viewName: "MP_PLAN_REVIEW", key: "mpVersion" },
};

/*
  전용 카드(AppCard)를 KPI 타일과 똑같이 **카드 전체 클릭 + 파란 보더 hover** 로 만든다.
  KpiCard 가 onClick 을 받았을 때 하는 일을 그대로 옮긴 것이다 — 한 화면에서 어떤 카드는
  제목만, 어떤 카드는 전체가 반응하면 "어디를 눌러야 하나"를 매번 시험해 보게 된다.

  ⚠️ 본문에 자기 클릭 대상이 있는 카드(결재선 트리)는 그 핸들러에서 stopPropagation 해야 한다.
    안 그러면 트리를 접으려다 화면이 넘어간다.

  제목 옆에 '새로 열기' 아이콘은 달지 않는다 — 카드 전체가 링크면 hover 하이라이트가 이미
  같은 말을 하고, KPI 타일 16종도 아이콘 없이 그 신호만 쓴다. 아이콘이 붙으면 그 카드만
  달라 보이는 데다 좁은 카드에서 제목을 밀어낸다(NPI 제목이 실제로 잘렸다).
*/
const drillCardSx = (onClick) => (onClick ? {
  cursor: "pointer",
  transition: CARD_HOVER_TRANSITION,
  "&:hover": CARD_HOVER_SX,
} : null);

/*
  카드 → 더 볼 화면(TabMenuList reduxKey). "이 숫자를 더 파려면 어디로 가나"를 한곳에 모은다.
  가능한 한 카드와 **같은 모듈** 화면으로 보낸다 — 카드가 이미 모듈 권한으로 걸러져 있으니
  그 안이면 열 수 있다. 예외는 bfRunStatus 하나로, BF 실행 상태를 보는 화면이 DP Process
  Management 뿐이다(이 카드는 지금도 dpProcessMgmtService 로 값을 읽는다).
*/
const CARD_DRILL = {
  meetingAgenda:  "MEETING_AGENDA",
  bfAccuracy:     "BF_ACCURACY_REPORT",
  bfDeviation:    "BF_ACCURACY_REPORT",
  bfRunStatus:    "DP_PROCESS_MGMT",
  dpPlanVsActual: "DP_ACTUAL_PLAN",
  dpPlanLift:     "DP_ACTUAL_PLAN",
  dpSalesTrend:   "DP_ACTUAL_PLAN",
  dpVersion:      "DP_WORKBENCH",
  ipTargetInv:    "TARGET_INV_SIMULATION_RESULTS",
  ipSafetyStock:  "TARGET_INV_SIMULATION_RESULTS",
  ipInventoryAmt: "INV_MGMT_OVERVIEW",
  rpFillRate:     "RP_REVIEW",
  rpOpenExc:      "INV_EXC",
  rpBackorder:    "RP_REVIEW",
  mpPlanRate:     "MP_PLAN_REVIEW",
  mpExceptions:   "MP_PRODUCTION_EXCEPTIONS",
  mpCapacity:     "RESOURCE_UTILIZATION",
};

/*
  카드 클릭 → 그 화면 탭 열기. 인사말 버전 칩(CHIP_DRILL)과 같은 방식이되 버전 프리셋은 없다.

  권한 없는 메뉴면 **undefined 를 돌려준다.** KpiCard 는 onClick 이 없으면 커서·hover 를 아예
  주지 않으므로, 눌러도 아무 일 없는 카드를 눌러보게 만들지 않는다. 카드 자체는 모듈 권한으로
  이미 걸러지지만 대상 화면의 appRoles 가 더 좁을 수 있어 한 번 더 본다.
*/
const useOpenView = (view) => {
  const dispatch = useDispatch();
  const { isPermitted } = useMenuAccess();
  // 판정은 사이드바와 같은 규칙 하나로 한다(useMenuAccess) — lv1·lv2·leaf 의 appRoles/userRoles 와
  //   테넌트별 제외 목록(excludes)까지 본다. 예전엔 여기서 leaf.appRoles 만 직접 봐서,
  //   "이 테넌트는 이 화면을 안 쓴다"로 빠진 화면이 홈 카드에서는 그대로 열렸다.
  return useMemo(() => {
    if (!view || !isPermitted(view)) return undefined;
    return () => dispatch(reduxUtil.updateHistoryState(view));
  }, [view, dispatch, isPermitted]);
};

const useCardDrill = (cardKey) => useOpenView(CARD_DRILL[cardKey]);

// 동기화 기준 스냅샷 칩
/*
  "기준" 칩 — 이 값이 언제 것인지. 카드마다 뜻이 다르다.
    · DW 카드   → 대상 기간을 칩에 두고, ETL 적재 시각은 tooltip 으로(둘을 한 칩에 넣으면 넘친다)
    · 실시간 카드 → 응답 받은 시각
  페이지 전체가 하나의 시각을 공유하던 구조(MOCK.sync)는 없앴다 — 카드마다 호출이 따로 끝나므로
  공통 시각은 어느 카드에도 맞지 않고, 목업 값이라 갱신도 안 됐다.
*/
/*
  카드마다 이 칩이 가리키는 게 다르다. 전부 "기준"으로만 쓰면 같은 뜻처럼 읽히므로 종류를 밝힌다.

    target   대상 월/기간 — 그 값이 **어느 기간을 잰 것인가**  (예: 2026-07 대상, 2026-08~10 대상)
    asOf     스냅샷 기준일 — 그 값이 **언제의 상태인가**        (예: 2026-08-09 기준)
    loaded   ETL 적재일   — 기간 개념이 없는 지표라 **언제 만든 값인가** (예: 2026-08-18 적재)

  날짜가 카드마다 다른 건 버그가 아니라 소스가 달라서다(§13 기간 규칙):
  DP/RP/MP 계획은 월 단위, IP 실재고는 일 단위 스냅샷, 조합 단위 지표는 기간 자체가 없다.
  라벨이 다르면 그 차이가 화면에서 바로 보인다.
*/
const CHIP_KIND_KEY = { target: "homeChipTarget", asOf: "homeAsOf", loaded: "homeChipLoaded" };
const CHIP_HINT_KEY = { target: "homeChipTargetHint", asOf: "homeChipAsOfHint", loaded: "homeChipLoadedHint" };

const AsOfChip = ({ tt, time, tooltip, kind = "asOf" }) => {
  const text = `${time} ${tt(CHIP_KIND_KEY[kind])}`;
  const chip = (
    /*
      칩은 카드 폭의 절반을 넘지 않는다. 예전엔 상한이 없어 긴 칩(버전명 + 실행 시각)이 제 폭을
      끝까지 지켰고, 좁은 카드에서 제목이 밀려 "Latest / BF / Run" 처럼 세 줄로 쪼개졌다 —
      제목이 먼저 읽혀야 무슨 카드인지 안다.
      ⚠️ 상한만 두고 그 아래로는 줄이지 않는다(flexShrink 0). 무조건 줄어들게 하면 자리가
        넉넉한 카드에서까지 "2026-09-01 loa…" 처럼 멀쩡한 칩이 잘린다.
      잘린 내용은 툴팁에 그대로 들어 있다.
    */
    <Typography sx={{ fontSize: FS.CAPTION, fontWeight: 600, color: "text.secondary", bgcolor: "action.hover", borderRadius: 1, px: 0.75, py: 0.25,
                      whiteSpace: "nowrap", flexShrink: 0, maxWidth: "50%", overflow: "hidden", textOverflow: "ellipsis" }}>
      {text}
    </Typography>
  );
  // 툴팁은 항상 붙인다 — 세 글자 라벨만으로는 "대상"과 "기준"의 차이를 처음 보는 사람이 모른다.
  //   칩이 잘렸을 때를 위해 보이는 문구를 맨 앞에 둔다.
  const title = [text, tt(CHIP_HINT_KEY[kind]), tooltip].filter(Boolean).join(" · ");
  return <Tooltip title={title}>{chip}</Tooltip>;
};

/*
  DP fact 의 "월" 은 이름표일 뿐 그 값이 덮는 기간이 아니다.
  DP 버킷은 버전마다 다르고(BUCKET_UNIT = month / week / partialWeek), **주 버킷은 월 경계를 걸친다**
  → 걸친 주가 시작일이 속한 달로 통째 들어가므로 그 "월" 이 28일 또는 35일짜리가 된다.
    예) week 버킷: "2026-03" 행 = 2026-03-02 ~ 2026-04-05 (5주 = 35일)

  ETL 이 실제 구간을 period_start/period_end 로 기록하고 sa_qty·bf_qty 를 그 구간으로 합산하므로
  **값은 맞다.** 문제는 표기다 — 칩에 "2026-03" 이라고만 쓰면 맞는 값을 잘못 읽게 된다.
  → 구간이 달력 월과 다를 때만 구간을 쓴다. month·partialWeek 테넌트는 화면이 그대로다.
    (RP·MP 카드가 이미 구간 칩을 쓰는 것과 같은 규약 — periodChip)
*/
const isCalendarMonth = (month, periodStart, periodEnd) => {
  if (!month || !periodStart || !periodEnd) return true;   // 구간을 모르면 종전대로 월로 본다
  const m = String(month).slice(0, 10);
  const [y, mo] = m.split("-").map(Number);
  if (!y || !mo) return true;
  // new Date(y, mo, 0) = 그 달의 마지막 날 (mo 는 1-based 라 다음 달의 0일)
  const lastDay = String(new Date(y, mo, 0).getDate()).padStart(2, "0");
  return String(periodStart).slice(0, 10) === m
      && String(periodEnd).slice(0, 10) === `${m.slice(0, 8)}${lastDay}`;
};

const targetPeriodChip = (tt, month, periodStart, periodEnd, refreshedTs) => {
  if (!month) return null;
  const exact = periodStart && periodEnd
    ? `${dateUtils.formatDate(periodStart, "yyyy-MM-dd")} ~ ${dateUtils.formatDate(periodEnd, "yyyy-MM-dd")}`
    : null;
  return (
    <AsOfChip
      tt={tt} kind="target"
      time={isCalendarMonth(month, periodStart, periodEnd)
        ? dateUtils.formatDate(month, "yyyy-MM")
        : `${dateUtils.formatDate(periodStart, "yyyy-MM-dd")}~${dateUtils.formatDate(periodEnd, "MM-dd")}`}
      /* 툴팁엔 **항상** 정확한 구간을 넣는다 — 칩이 월로 보일 때도 확인할 수 있어야 한다. */
      tooltip={[exact, refreshedTs ? `${tt("homeDwRefreshed")} ${dateUtils.formatDate(refreshedTs, "yyyy-MM-dd HH:mm")}` : null]
        .filter(Boolean).join(" · ") || null}
    />
  );
};

// 파이프라인 단계 배지
const StageBadge = ({ tt, badge, compact = false }) => {
  const theme = useTheme();
  // 좁은 폭에서는 "확인 5건" → "5" 처럼 숫자만(공간 절약). count 없는 상태는 라벨 그대로.
  const label = badge.count != null
    ? (compact ? `${badge.count}` : `${tt(badge.key)} ${badge.count}${tt("homeCountUnit")}`)
    : tt(badge.key);
  const style = {
    ok: { color: "success.main", bgcolor: alpha(theme.palette.success.main, 0.14) },
    info: { color: "primary.main", bgcolor: alpha(theme.palette.primary.main, 0.12) },   // 진행중 — 정상 작업이라 경고색이 아님
    warn: { color: "warning.main", bgcolor: alpha(theme.palette.warning.main, 0.16) },
    prep: { color: "text.secondary", bgcolor: "action.hover", border: "1px dashed", borderColor: "divider" },
  }[badge.variant];
  return (
    <Typography sx={{ fontSize: compact ? FS.CAPTION : FS.BODY, fontWeight: 600, borderRadius: 5, flexShrink: 0, px: compact ? 0.75 : 1, py: compact ? 0.2 : 0.375, whiteSpace: "nowrap", ...style }}>
      {label}
    </Typography>
  );
};

/*
 * 카드 카탈로그 — 각 카드: { key, module, role?, span, rowSpan?, render(ctx) }.
 *   module="core" 는 항상, 그 외는 ctx.has(role) 일 때만 렌더된다.
 *   ctx = { tt, username, today, permitted(=권한 있는 MODULES), roles, versions, versionsLoading,
 *           onVersionClick, approvalAcOn }
 *   "기준" 시각은 ctx 에 없다 — 카드마다 뜻이 달라 각자 구한다(AsOfChip 주석 참고).
 */
// '내 할 일' 카드 — 즐겨찾기한 개인 워크플로우 + 진행률(done/total). 클릭 시 워크플로우 패널 열기.
//   워크플로우는 z_p13n "MY_WORKFLOW" 에 저장(WorkflowPanel 과 동일 소스): { workflows:[{id,name,steps:[{done}],favorite}] }
const WF_PAGE_CD = "MY_WORKFLOW";

const MyTasksCard = ({ tt, tall }) => {
  const dispatch = useDispatch();
  const { isPermitted } = useMenuAccess();
  const [favorites, setFavorites] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    p13nService.get(WF_PAGE_CD)
      .then((res) => setFavorites((res?.data?.workflows ?? []).filter((w) => w.favorite)))
      .catch((e) => debug(e))
      .finally(() => setLoaded(true));
  }, []);

  const remaining = favorites.reduce(
    (n, w) => n + ((w.steps?.length ?? 0) - (w.steps?.filter((s) => s.done).length ?? 0)), 0);
  // AI 어시스턴트 카드에 신고 — 아래 '남음' 배지와 **같은 조건**을 쓴다(임계치를 새로 만들지 않는다).
  useReportHomeSignal("myTasks", () => (remaining > 0
    ? { cardKey: "myTasks", module: null, kind: "tasksRemaining", severity: "info", count: remaining }
    : null), [remaining]);

  // 작업이 속한 페이지 칩 — 클릭 시 해당 탭 열기(행/헤더 클릭과 분리).
  const openMenu = (menuKey) => window.dispatchEvent(new CustomEvent("plannel:openMenu", { detail: { key: menuKey } }));
  const pageChip = (menuKey) => {
    if (menuKey == null || !isPermitted(menuKey)) return null;
    const leaf = tabMenuList.findLv3Menu(menuKey);
    const label = leaf ? (leaf.skipI18n ? leaf.title : tt(leaf.title)) : null;
    if (!label) return null;
    return (
      <Typography component="span" title={label}
        onClick={(e) => { e.stopPropagation(); openMenu(menuKey); }}
        sx={{ flexShrink: 0, maxWidth: 120, fontSize: FS.CAPTION, fontWeight: 600, color: "primary.main", bgcolor: (th) => alpha(th.palette.primary.main, 0.1), borderRadius: 1, px: 0.6, py: 0.1, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", "&:hover": { textDecoration: "underline" } }}>
        {label}
      </Typography>
    );
  };

  return (
    <AppCard sx={{ p: 2, flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <CardIconBadge Icon={ChecklistIcon} hex={coreHex()} loading={!loaded} />
          <Typography sx={CARD_HEADER_SX}>{tt("homeMyTasks")}</Typography>
        </Box>
        {remaining > 0 && (
          <Typography sx={{ fontSize: FS.BODY, fontWeight: 600, color: "error.main", bgcolor: (th) => alpha(th.palette.error.main, 0.12), borderRadius: 5, px: 1, py: 0.25 }}>
            {`${remaining}${tt("homeCountUnit")} ${tt("homeRemaining")}`}
          </Typography>
        )}
      </Box>
      {loaded && favorites.length === 0 && (
        <Typography sx={{ fontSize: FS.SUB, color: "text.secondary", mt: 1 }}>{tt("homeNoFavoriteWorkflow")}</Typography>
      )}
      {/* 1×2 이상(tall) 이면 전체 단계 목록(process 전부, 스크롤), 1×1 이면 워크플로우별 "다음 단계"만 컴팩트하게 */}
      <Box sx={{ minWidth: 0, ...(tall ? { flex: 1, minHeight: 0, overflowY: "auto" } : {}) }}>
        {favorites.map((wf) => {
          const total = wf.steps?.length ?? 0;
          const done = wf.steps?.filter((s) => s.done).length ?? 0;
          const complete = total > 0 && done === total;

          if (tall) {
            // 전체 프로세스: 워크플로우명 헤더 + 모든 단계(완료 체크·페이지 칩)
            return (
              <Box key={wf.id} sx={{ mb: 1 }}>
                <Box onClick={() => dispatch(openWorkflowPanel(wf.id))}
                  sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.4, cursor: "pointer", borderRadius: 1, "&:hover": { bgcolor: "action.hover" } }}>
                  <StarIcon sx={{ fontSize: 15, color: "#F5B300", flexShrink: 0 }} />
                  <Typography sx={{ flex: 1, minWidth: 0, fontSize: FS.TITLE, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{wf.name}</Typography>
                  {total > 0 && <Typography sx={{ fontSize: FS.SUB, color: "text.secondary", flexShrink: 0 }}>{`${done}/${total}`}</Typography>}
                </Box>
                {wf.steps?.map((step) => (
                  <Box key={step.id} sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.2, pl: 3, minWidth: 0 }}>
                    {step.done
                      ? <CheckCircleIcon sx={{ fontSize: 14, color: "success.main", flexShrink: 0 }} />
                      : <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0 }} />}
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: FS.SUB, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: step.done ? "text.disabled" : "text.primary", textDecoration: step.done ? "line-through" : "none" }}>
                      {step.text}
                    </Typography>
                    {pageChip(step.menuKey)}
                  </Box>
                ))}
              </Box>
            );
          }

          // 컴팩트: 워크플로우별 "다음 단계"만
          const nextStep = wf.steps?.find((s) => !s.done);   // 다음 해야 할 작업 = 첫 미완료 step
          return (
            <Box key={wf.id} onClick={() => dispatch(openWorkflowPanel(wf.id))}
              sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, cursor: "pointer", borderRadius: 1, "&:hover": { bgcolor: "action.hover" } }}>
              <StarIcon sx={{ fontSize: 15, color: "#F5B300", flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* 워크플로우명은 캡션, 메인은 "다음 단계" 작업 텍스트 + 속한 페이지 칩(클릭 시 이동) */}
                <Typography sx={{ fontSize: FS.BODY, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{wf.name}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                  <Typography sx={{ minWidth: 0, fontSize: FS.TITLE, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: complete ? "text.disabled" : "text.primary", textDecoration: complete ? "line-through" : "none" }}>
                    {complete ? tt("homeTasksAllDone") : (nextStep?.text ?? wf.name)}
                  </Typography>
                  {!complete && pageChip(nextStep?.menuKey)}
                </Box>
              </Box>
              {total > 0 && <Typography sx={{ fontSize: FS.SUB, color: "text.secondary", flexShrink: 0 }}>{`${done}/${total}`}</Typography>}
            </Box>
          );
        })}
      </Box>
    </AppCard>
  );
};

// 홈에 고정 가능한 대시보드 — "Dashboard 메뉴(SNOP + DASHBOARD 그룹)" 중 목업(dashboard_temp)을 제외한 실 페이지만.
//   목업 세트는 늘지 않으므로 부정 목록(제외)이 실 페이지 추가 시 자동 노출되어 유지보수에 유리.
//   DW 실화면으로 대체된 5개(판매현황·판매계획vs실적·재고·공급계획·거점별재고)는 목업째 삭제돼 목록에서도 빠졌다.
//   ⚠ 2026-08-28 부터 아래 6개는 TabMenuList 등록 자체가 빠져서 이 필터에 걸릴 일이 없다(무해한 잔여 가드).
//     목업을 다시 메뉴에 올리면 그때 이 목록이 다시 제 역할을 한다 — 그래서 지우지 않고 둔다.
const MOCK_DASHBOARD_KEYS = new Set([
  "SALES_PLAN_DASHBOARD",
  "SUPPLY_TREND_DASHBOARD", "FP_PLAN_AGAINST_PRFM_DASHBOARD", "FP_PLAN_AGAINST_PRFM_DETAIL_DASHBOARD",
  "PSI_BALANCE_DASHBOARD", "RTF_FULFILLMENT_DASHBOARD",
]);
/*
  같은 메뉴 그룹에 있지만 **대시보드가 아닌** 화면.
  SNOP 그룹은 "숫자를 보는 화면" 모음이었는데 회의 안건이 그 첫 줄로 옮겨 왔다(2026-09-18).
  걸러 내지 않으면 '고정할 대시보드' 선택지에 안건 화면이 끼는데, 다른 대시보드는 화면 안 ★ 로도
  고정할 수 있는 반면 이건 이 모달 말고는 고정할 자리가 없어 혼자만 다르게 동작한다.
  회의 안건은 홈에 전용 카드(meetingAgenda)가 따로 있다.
*/
const NON_DASHBOARD_KEYS = new Set(["MEETING_AGENDA"]);
// 접근 권한 있는 실 대시보드만 → [{ viewName, title(i18n key) }]
//   판정은 사이드바와 같은 규칙(isPermitted) — appRoles 뿐 아니라 userRoles·테넌트 제외 목록도 본다.
const getPinnableDashboards = (isPermitted) =>
  [...(tabMenuList.getLv3MenuList("SNOP") ?? []), ...(tabMenuList.getLv3MenuList("DASHBOARD") ?? [])]
    .filter((l) => l.reduxKey && !MOCK_DASHBOARD_KEYS.has(l.reduxKey) && !NON_DASHBOARD_KEYS.has(l.reduxKey))
    .filter((l) => isPermitted(l.reduxKey))
    .map((l) => ({ viewName: l.reduxKey, title: l.title }));

// '고정한 대시보드' 카드 — 대시보드에서 ★ 로 고정한 목록(z_p13n "PINNED_DASHBOARD"). 클릭 시 해당 대시보드 탭 열기.
//   헤더 + 버튼으로 고정 가능 목록(권한 필터·목업 제외) 모달을 열어 여기서도 직접 고정/해제.
const PinnedCard = ({ tt }) => {
  const dispatch = useDispatch();
  const { isPermitted } = useMenuAccess();
  const [pinned, setPinned] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  useEffect(() => {
    p13nService.get(PINNED_PAGE_CD)
      .then((res) => setPinned(res?.data?.dashboards ?? []))
      .catch((e) => debug(e))
      .finally(() => setLoaded(true));
  }, []);

  const pinnable = getPinnableDashboards(isPermitted);
  // 이미 고정해 둔 목록에도 같은 필터를 건다 — 권한이 나중에 빠져도, 목록에서 빠진 화면이어도
  //   (메뉴 이동으로 대시보드가 아니게 된 경우 등) 저장된 항목은 그대로 남아 있다.
  const shownPinned = pinned.filter((v) => isPermitted(v) && !NON_DASHBOARD_KEYS.has(v));
  const open = (viewName) => dispatch(reduxUtil.updateHistoryState(viewName));

  const handleSave = (next) => {
    setPinned(next);                 // 낙관적 반영
    setManageOpen(false);
    p13nService.upsert({ pageCd: PINNED_PAGE_CD, configObj: { dashboards: next } }).catch((e) => debug(e));
  };

  return (
    <AppCard sx={{ p: 2, flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <CardIconBadge Icon={DashboardIcon} hex={coreHex()} loading={!loaded} />
        <Typography sx={CARD_HEADER_SX}>{tt("homePinned")}</Typography>
        <Tooltip title={tt("homePinnedManage")}>
          <IconButton size="small" sx={{ ml: "auto" }} onClick={() => setManageOpen(true)} aria-label="manage pinned dashboards">
            <AddIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
      {loaded && shownPinned.length === 0 && (
        <Typography sx={{ fontSize: FS.SUB, color: "text.secondary", mt: 1 }}>{tt("homeNoPinnedDashboard")}</Typography>
      )}
      <Box sx={{ mt: 0.5 }}>
        {shownPinned.map((viewName) => (
          <Box key={viewName} onClick={() => open(viewName)}
            sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, cursor: "pointer", borderRadius: 1, "&:hover": { bgcolor: "action.hover" } }}>
            <StarIcon sx={{ fontSize: 15, color: "#F5B300", flexShrink: 0 }} />
            <Typography sx={{ flex: 1, minWidth: 0, fontSize: FS.TITLE, color: "primary.main", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {tabMenuList.getLocalizedTitle(viewName) ?? viewName}
            </Typography>
          </Box>
        ))}
      </Box>
      <DashboardPinModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        title={tt("homePinnedManage")}
        emptyLabel={tt("homeNoPinnableDashboard")}
        dashboards={pinnable}
        pinned={pinned}
        onSave={handleSave}
      />
    </AppCard>
  );
};

/*
  '회의 안건' 카드 — **나에게 배정된** 안건 중 오늘부터 일주일 안에 열리는 회의.
  Meeting Agenda 화면의 "내 안건" 탭에 회의 시간 범위를 건 것과 같은 조회다
  (assignedToMe + meetingFrom/meetingTo). 카드가 자체 임계치를 만들지 않는다.

  ⚠️ 기간 칩을 **카드가 직접** 세운다. DW 카드는 서버가 구간(fromMonth/toMonth)을 돌려주지만
    여기서는 범위를 카드가 정하므로 칩도 카드가 만든다. 칩이 없으면 "왜 다음 달 회의가
    안 보이나"를 화면에서 알 길이 없다.

  DRAFT·CLOSED 는 뺀다 — DRAFT 는 아직 공유 전이라 배정이 확정이 아니고(작성자 본인에게만
  보인다), CLOSED 는 이미 끝난 회의다. 둘 다 "앞으로 준비할 회의" 가 아니다.
*/
const AGENDA_DAYS_AHEAD = 7;
const AGENDA_STATUS_CDS = ["PUBLISHED", "IN_REVIEW"];
// 카드가 스크롤로 다 보여줄 수 있는 만큼만. 총 건수는 서버가 주는 totalItems 를 쓴다.
const AGENDA_FETCH_SIZE = 20;

/*
  서버가 받는 LocalDateTime 문자열.
  ⚠️ Date 객체로 보내면 restApi 가 yyyy-MM-dd 로 직렬화해 시각이 잘린다 — 끝 날짜가 자정이 되어
    마지막 날 오후 회의가 통째로 빠진다. 문자열로 23:59:59 까지 늘린다(Meeting Agenda 화면과 같은 규칙).
*/
const agendaDateTime = (date, endOfDay) =>
  `${dateUtils.formatDate(date, "yyyy-MM-dd")}T${endOfDay ? "23:59:59" : "00:00:00"}`;

const MeetingAgendaCard = ({ tt }) => {
  const dispatch = useDispatch();
  /*
    카드 클릭 → 안건 화면의 **"나에게 배정" 탭**. 카드가 보여 준 목록이 그 탭의 목록이므로,
    전체 목록으로 떨어뜨리면 방금 본 회의를 화면에서 다시 찾아야 한다.

    두 경로를 다 밟는다. 하나만 두면 나머지 경우에 탭이 안 바뀐다:
      · redux 뷰 상태 — 탭이 **아직 없을 때**. 화면이 mount 하며 초기 탭으로 읽는다.
      · 이벤트       — 탭이 **이미 열려 있을 때**. 그 화면은 다시 mount 되지 않는다
                       (TabContainer 는 비활성 탭을 숨길 뿐이다) → 초기값을 읽을 기회가 없다.
    순서도 이대로여야 한다 — 프리셋이 탭을 여는 dispatch 보다 앞서야 새 탭이 그 값을 보고 뜬다.
    다른 필터(검색어·기간)는 건드리지 않는다. 화면 상단에 그대로 보이는 사용자 설정이라
    카드가 말없이 지울 것이 아니다.
  */
  const openPage = useCardDrill("meetingAgenda");   // 권한 판정 + 탭 열기
  const openAssignedToMe = openPage ? () => {
    // 대상 화면(reduxKey)은 CARD_DRILL 이 단일 소스다 — 같은 문자열을 두 번 적으면 한쪽만 바뀐다
    dispatch(reduxUtil.updateViewState(
      reduxUtil.createObjectState(CARD_DRILL.meetingAgenda, { scope: AGENDA_SCOPE.MINE, currentPage: 1 })));
    openPage();
    window.dispatchEvent(new CustomEvent(AGENDA_SCOPE_EVENT, { detail: { scope: AGENDA_SCOPE.MINE } }));
  } : undefined;
  const [agendas, setAgendas] = useState([]);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);

  // 오늘 0시 ~ +7일 24시. 카드가 떠 있는 동안 자정을 넘겨도 다시 조회하지 않는다(새로고침 때 따라온다).
  const [from, to] = useMemo(() => {
    const end = new Date();
    end.setDate(end.getDate() + AGENDA_DAYS_AHEAD);
    return [new Date(), end];
  }, []);

  useEffect(() => {
    meetingAgendaService.list({
      assignedToMe: true,
      statusCds: AGENDA_STATUS_CDS,
      meetingFrom: agendaDateTime(from, false),
      meetingTo: agendaDateTime(to, true),
      page: 0,
      pageSize: AGENDA_FETCH_SIZE,
    })
      .then((res) => {
        setAgendas(res?.data?.results ?? []);
        setTotal(res?.data?.totalItems ?? 0);
      })
      .catch((e) => debug(e))
      .finally(() => setLoaded(true));
  }, [from, to]);

  const todayKey = dateUtils.formatDate(from, "yyyy-MM-dd");

  return (
    <AppCard onClick={openAssignedToMe} sx={{ p: 2, flex: 1, minWidth: 0, display: "flex", flexDirection: "column", ...drillCardSx(openAssignedToMe) }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <CardIconBadge Icon={EventNoteIcon} hex={coreHex()} loading={!loaded} />
          <Typography className={CARD_TITLE_CLASS} sx={CARD_HEADER_SX}>{tt("homeMeetingAgenda")}</Typography>
        </Box>
        {/* 기간 칩 — 다른 카드와 같은 자리·같은 규격. 이 카드의 '대상' 은 회의 시간 범위다. */}
        <AsOfChip tt={tt} kind="target"
          time={`${dateUtils.formatDate(from, "MM-dd")}~${dateUtils.formatDate(to, "MM-dd")}`}
          tooltip={`${dateUtils.formatDate(from, "yyyy-MM-dd")} ~ ${dateUtils.formatDate(to, "yyyy-MM-dd")}`} />
      </Box>
      <Typography sx={{ fontSize: FS.SUB, color: "text.secondary", mt: 0.25 }}>
        {total > 0
          ? `${tt("homeMeetingAgendaSubtitle")} · ${total.toLocaleString()}${tt("homeCountUnit")}`
          : tt("homeMeetingAgendaSubtitle")}
      </Typography>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", mt: 0.75 }}>
        {loaded && agendas.length === 0 && (
          <Typography sx={{ fontSize: FS.SUB, color: "text.secondary" }}>{tt("homeNoMeetingAgenda")}</Typography>
        )}
        {/* 정렬은 서버가 회의 시간 오름차순으로 준다 — 맨 위가 제일 가까운 회의다. */}
        {agendas.map((a) => {
          const isToday = dateUtils.formatDate(a.meetingStartTs, "yyyy-MM-dd") === todayKey;
          return (
            <Box key={a.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.4, minWidth: 0 }}>
              {/* 오늘 회의만 색을 준다 — 경고가 아니라 '오늘' 이라는 뜻이라 primary 다. */}
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, bgcolor: isToday ? "primary.main" : "text.disabled" }} />
              <Typography sx={{ flex: 1, minWidth: 0, fontSize: FS.TITLE, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {a.title}
              </Typography>
              {/*
                오늘 회의는 날짜 대신 "오늘" 이라고 쓴다 — 목록에서 눈이 찾는 건 날짜가 아니라
                "지금 준비해야 하나" 이고, 그건 날짜를 오늘과 맞춰 봐야 알 수 있다.
                다른 날은 날짜 그대로 둔다(내일·모레까지 말로 바꾸면 그 셋을 서로 구분하기가 더 어렵다).
              */}
              <Typography sx={{ fontSize: FS.BODY, flexShrink: 0, whiteSpace: "nowrap", fontWeight: isToday ? 600 : 400, color: isToday ? "primary.main" : "text.secondary" }}>
                {isToday
                  ? `${tt("homeToday")} ${dateUtils.formatDate(a.meetingStartTs, "HH:mm")}`
                  : dateUtils.formatDate(a.meetingStartTs, "MM-dd HH:mm")}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </AppCard>
  );
};

// '알림' 카드 — notification-service 의 사용자 알림(미읽음 우선). 벨 팝오버와 동일 소스.
const NotificationsCard = ({ tt }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    notificationService.getNotifications()
      // 응답 형태는 NotificationBell 과 동일: res.data.data.results (또는 res.data.results)
      .then((res) => setItems(res?.data?.data?.results ?? res?.data?.results ?? []))
      .catch((e) => debug(e))
      .finally(() => setLoaded(true));
  }, []);

  // 옵션 B — 안읽음만 표시(읽으면 카드에서 사라짐). 다 읽으면 "새 알림 없음".
  const unreadItems = items.filter((n) => !n.readFlg);
  const unread = unreadItems.length;
  const show = unreadItems.slice(0, 5);
  useReportHomeSignal("notifications", () => (unread > 0
    ? { cardKey: "notifications", module: null, kind: "unreadNotifications", severity: "info", count: unread }
    : null), [unread]);
  const dotColor = (type) => ({ error: "error.main", warning: "warning.main", success: "success.main", maintenance: "warning.main", "maintenance-notice": "warning.main" }[type] ?? "primary.main");

  // 알림 클릭 → 읽음 처리(낙관적: 먼저 목록에서 제거 → 서버 반영). 안읽음만 보이므로 자연히 사라진다.
  const markRead = (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, readFlg: true } : n)));
    notificationService.markNotificationAsRead(id).catch((e) => debug(e));
  };

  return (
    <AppCard sx={{ p: 2, flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <CardIconBadge Icon={NotificationsIcon} hex={coreHex()} loading={!loaded} />
          <Typography sx={CARD_HEADER_SX}>{tt("homeNotifications")}</Typography>
        </Box>
        {unread > 0 && (
          <Typography sx={{ fontSize: FS.BODY, fontWeight: 600, color: "error.main", bgcolor: (th) => alpha(th.palette.error.main, 0.12), borderRadius: 5, px: 1, py: 0.25 }}>
            {`${unread} ${tt("homeUnread")}`}
          </Typography>
        )}
      </Box>
      {loaded && unread === 0 && (
        <Typography sx={{ fontSize: FS.SUB, color: "text.secondary", mt: 1 }}>{tt("homeNoNotifications")}</Typography>
      )}
      <Box sx={{ mt: 0.5 }}>
        {show.map((n) => (
          <Box key={n.id} onClick={() => markRead(n.id)}
            sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 0.5, px: 0.75, mx: -0.75, minWidth: 0, borderRadius: 1, cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, mt: 0.7, bgcolor: dotColor(n.type) }} />
            <Typography sx={{ flex: 1, minWidth: 0, fontSize: FS.TITLE, fontWeight: 500, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {t(n.title)}
            </Typography>
          </Box>
        ))}
      </Box>
    </AppCard>
  );
};

// 승인 상태 — DP Process Management 의 apvlStatusCd 와 동일 개념. 색/라벨 매핑.
const APVL_STATUS = {
  NOTSTARTED: { key: "homeApvlNotStarted", color: "text.disabled" },
  INPROGRESS: { key: "homeApvlInProgress", color: "warning.main" }, // 내 액션 필요(승인 대기)
  COMPLETED: { key: "homeApvlCompleted", color: "success.main" },
};

// 승인 상태 카운트(미완료=대기/미시작) — 트리 재귀.
const countPendingApvl = (node) => (node.status !== "COMPLETED" ? 1 : 0) + (node.children ?? []).reduce((n, c) => n + countPendingApvl(c), 0);

/*
  평탄한 포지션 목록 → 트리. 서버가 주는 postnPath 는 [자신, 상위, …, 최종승인자] 순이라
  path[1] 이 직속 상위다(PostnService.makePostnPath 가 자신부터 넣고 부모로 올라간다).
  상위를 못 찾은 노드는 뿌리로 둔다 — 접근제어 범위 밖 상위가 목록에서 빠질 수 있고,
  그때 노드를 버리면 승인 대기 건이 화면에서 사라진다.
*/
const buildApprovalTree = (positions = []) => {
  const byCd = new Map();
  positions.forEach((p) => byCd.set(p.postnCd, { ...p, status: p.statusCd, children: [] }));

  const roots = [];
  positions.forEach((p) => {
    const node = byCd.get(p.postnCd);
    const parent = byCd.get(p.postnPath?.[1]);
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  });
  return roots;
};

// 트리에서 내 포지션 찾기(깊이 우선). 여러 포지션을 맡았으면 먼저 만나는 것 하나만 헤더로 쓴다.
const findApvlNode = (nodes, match) => {
  for (const node of nodes) {
    if (match(node)) return node;
    const found = findApvlNode(node.children ?? [], match);
    if (found) return found;
  }
  return null;
};

// 승인 카드 → DP Process Management 이동 대상(reduxKey=viewName).
const DP_PROCESS_VIEW = "DP_PROCESS_MGMT";
// 접근제어가 꺼져 있으면 승인 대기 카드는 "여기서 켤 수 있다"는 뜻으로 이 화면을 연다.
const ACCESS_CONTROL_VIEW = "DP_ACCESSCONTROLADMIN";

// '승인 대기' 카드 — 접근제어(APPROVAL_ACCESS_CONTROL)는 DP plan config 라 이 카드도 DP 모듈 소속이다.
//   회사가 접근제어를 켠 경우에만 내용 표시(게이팅은 Home, enabled).
//   내 포지션 상태(미완료면 알림) + 하위 포지션 전체를 접이식 트리로. 초기엔 직속 자식만 펼침(더 아래는 접힘).
const ApprovalsCard = ({ tt, enabled = true, moduleKey }) => {
  /*
    카드 클릭 → 접근제어가 켜져 있으면 결재 현황(DP Process Management), 꺼져 있으면 접근제어 설정.
    꺼졌을 때도 링크를 두는 이유: 카드가 "꺼져 있어요"라고만 말하고 끝나면 어디서 켜는지를
    사용자가 따로 찾아야 한다. 카드 자체가 그 자리로 데려가는 게 맞다.
    ⚠️ 이름이 open 이 아닌 이유 — 아래 renderTree 안에 접힘 상태를 뜻하는 지역 open 이 있다.
  */
  const openProcess = useOpenView(DP_PROCESS_VIEW);
  const openAccessControl = useOpenView(ACCESS_CONTROL_VIEW);
  const openTarget = enabled ? openProcess : openAccessControl;
  const [apvl, setApvl] = useState({ loaded: false, positions: [], myCds: [] });
  const [expanded, setExpanded] = useState(() => new Set()); // 펼친 노드 postnCd — 비어있으면 직속 자식만 보임
  const toggle = (cd) => setExpanded((prev) => { const next = new Set(prev); if (next.has(cd)) next.delete(cd); else next.add(cd); return next; });

  useEffect(() => {
    // 접근제어가 꺼져 있으면 카드가 내용을 안 보여주므로 조회도 하지 않는다.
    if (!enabled) { setApvl({ loaded: true, positions: [], myCds: [] }); return; }
    dpProcessMgmtService.getApprovalStatus()
      .then((res) => setApvl({
        loaded: true,
        positions: res?.data?.positions ?? [],
        myCds: res?.data?.myPostnCds ?? [],
      }))
      .catch((e) => { debug(e); setApvl({ loaded: true, positions: [], myCds: [] }); });
  }, [enabled]);

  const roots = buildApprovalTree(apvl.positions);
  /*
    내 포지션을 찾으면 그것을 헤더로 세우고 그 아래만 트리로 편다 — 내가 결재선의 어디에 있고
    누구를 기다리는지가 이 카드의 목적이다. 내 포지션이 결재선에 없으면(승인 권한 없는 사용자)
    전체 뿌리부터 보여 준다 — 빈 카드보다는 현황이라도 보이는 편이 낫다.
  */
  const myPosition = findApvlNode(roots, (n) => apvl.myCds.includes(n.postnCd));
  const children = myPosition ? myPosition.children : roots;

  const statusOf = (s) => APVL_STATUS[s] ?? APVL_STATUS.NOTSTARTED;
  const pending = (myPosition && myPosition.status !== "COMPLETED" ? 1 : 0) + children.reduce((n, c) => n + countPendingApvl(c), 0);
  const myAlert = !!(myPosition && myPosition.status !== "COMPLETED");
  // 내 포지션이 미완료면 "내가 막고 있다"는 뜻이라 error, 아래 결재선이 남은 것뿐이면 warn.
  useReportHomeSignal("approvals", () => (pending > 0
    ? { cardKey: "approvals", module: moduleKey, kind: "apvlPending",
        severity: myAlert ? "error" : "warn", count: pending }
    : null), [pending, myAlert, moduleKey]);

  const StatusLabel = ({ status }) => (
    <Typography sx={{ fontSize: FS.BODY, fontWeight: 600, flexShrink: 0, color: statusOf(status).color }}>{tt(statusOf(status).key)}</Typography>
  );

  // 트리 렌더(재귀·평탄화) — depth 로 들여쓰기, 자식 있으면 chevron 토글, 펼친 노드만 자식 노출.
  const renderTree = (nodes, depth) => nodes.flatMap((node) => {
    const hasKids = (node.children?.length ?? 0) > 0;
    const open = expanded.has(node.postnCd);
    const row = (
      <Box key={node.postnCd}
        // 카드 전체가 링크라, 접기/펼치기 클릭이 위로 올라가면 화면이 넘어간다.
        onClick={hasKids ? (e) => { e.stopPropagation(); toggle(node.postnCd); } : undefined}
        sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.4, pl: 1 + depth * 2, pr: 0.5, minWidth: 0, borderRadius: 1,
          cursor: hasKids ? "pointer" : "default", ...(hasKids && { "&:hover": { bgcolor: "action.hover" } }) }}>
        {hasKids
          ? (open ? <ExpandMoreIcon sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }} /> : <ChevronRightIcon sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }} />)
          : <Box sx={{ width: 16, flexShrink: 0 }} />}
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, bgcolor: statusOf(node.status).color }} />
        <Typography sx={{ flex: 1, minWidth: 0, fontSize: FS.TITLE, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.name}</Typography>
        <StatusLabel status={node.status} />
      </Box>
    );
    return hasKids && open ? [row, ...renderTree(node.children, depth + 1)] : [row];
  });

  return (
    <AppCard onClick={openTarget}
      sx={{ p: 2, flex: 1, minWidth: 0, display: "flex", flexDirection: "column", ...drillCardSx(openTarget) }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <CardIconBadge Icon={AccountTreeIcon} hex={moduleHex(moduleKey)} loading={!apvl.loaded} />
          <Typography className={CARD_TITLE_CLASS} sx={CARD_HEADER_SX}>{tt("homeApprovals")}</Typography>
        </Box>
        {enabled && pending > 0 && (
          <Typography sx={{ fontSize: FS.BODY, fontWeight: 600, color: "warning.main", bgcolor: (th) => alpha(th.palette.warning.main, 0.14), borderRadius: 5, px: 1, py: 0.25 }}>
            {`${pending}${tt("homeCountUnit")}`}
          </Typography>
        )}
      </Box>

      {!enabled ? (
        // 접근제어 OFF — 카드는 유지, 내용은 안내 문구만
        <Typography sx={{ fontSize: FS.SUB, color: "text.secondary", mt: 1 }}>{tt("homeApprovalsOff")}</Typography>
      ) : (myPosition || children.length > 0) ? (
        <>
          {/* 내 포지션 — 미완료면 알림(warning 틴트) */}
          {myPosition && (
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.75, mt: 0.5, borderRadius: 1, minWidth: 0, flexShrink: 0,
              ...(myAlert && { bgcolor: (th) => alpha(th.palette.warning.main, 0.1), border: "1px solid", borderColor: (th) => alpha(th.palette.warning.main, 0.3) }),
            }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: statusOf(myPosition.status).color }} />
              <Typography sx={{ flex: 1, minWidth: 0, fontSize: FS.TITLE, fontWeight: 600, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {myPosition.name}
              </Typography>
              <Typography component="span" sx={{ fontSize: FS.CAPTION, color: "text.secondary", flexShrink: 0, mr: 0.5 }}>{tt("homeMyPosition")}</Typography>
              <StatusLabel status={myPosition.status} />
            </Box>
          )}

          {/* 하위 포지션 — 접이식 트리(넘치면 카드 내부 스크롤) */}
          {children.length > 0 && (
            <>
              <Typography sx={{ fontSize: FS.BODY, color: "text.secondary", mt: 1, mb: 0.25, flexShrink: 0 }}>{tt("homeChildPositions")}</Typography>
              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>{renderTree(children, 0)}</Box>
            </>
          )}
        </>
      ) : (
        <Typography sx={{ fontSize: FS.SUB, color: "text.secondary", mt: 1 }}>{tt("homeNoApprovals")}</Typography>
      )}
    </AppCard>
  );
};

// '신제품(NPI)' 카드 — z_item.npi_flg='Y' 기준. 유사품목 프로파일이 없는 신제품을 우선 노출한다
//   (프로파일이 없으면 예측 근거가 없어 계획이 조용히 틀어지므로 확인 대상). 클릭 → NPI History.
const NPI_VIEW = "DP_NPI_HISTORY";   // TabMenuList reduxKey

const NpiCard = ({ tt, moduleKey }) => {
  const [npi, setNpi] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    itemService.getNpiSummary()
      .then((res) => setNpi(res?.data ?? null))
      .catch((e) => debug(e))
      .finally(() => setLoaded(true));
  }, []);

  const total = npi?.total ?? 0;
  const missingCount = npi?.missingCount ?? 0;
  const missing = npi?.missing ?? [];
  const open = useOpenView(NPI_VIEW);
  useReportHomeSignal("npi", () => (missingCount > 0
    ? { cardKey: "npi", module: moduleKey, kind: "npiNoProfile", severity: "warn", count: missingCount }
    : null), [missingCount, moduleKey]);

  return (
    <AppCard onClick={open} sx={{ p: 2, flex: 1, minWidth: 0, display: "flex", flexDirection: "column", ...drillCardSx(open) }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <CardIconBadge Icon={NewReleasesIcon} hex={moduleHex(moduleKey)} loading={!loaded} />
          <Typography className={CARD_TITLE_CLASS} sx={CARD_HEADER_SX}>{tt("homeNpi")}</Typography>
        </Box>
        {missingCount > 0 && (
          <Typography sx={{ fontSize: FS.BODY, fontWeight: 600, flexShrink: 0, color: "warning.main", bgcolor: (th) => alpha(th.palette.warning.main, 0.14), borderRadius: 5, px: 1, py: 0.25 }}>
            {`${tt("homeNpiNoProfile")} ${missingCount}${tt("homeCountUnit")}`}
          </Typography>
        )}
      </Box>
      {/* 부제 — 이 카드가 무엇을 보여주는지 명확히 */}
      <Typography sx={{ fontSize: FS.SUB, color: "text.secondary", mt: 0.25 }}>{tt("homeNpiSubtitle")}</Typography>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", mt: 0.75 }}>
        {npi && total === 0 && (
          <Typography sx={{ fontSize: FS.SUB, color: "text.secondary" }}>{tt("homeNpiNone")}</Typography>
        )}
        {npi && total > 0 && missingCount === 0 && (
          <Typography sx={{ fontSize: FS.SUB, color: "success.main" }}>{tt("homeNpiAllSet")}</Typography>
        )}
        {missing.map((it) => (
          <Box key={it.itemCd} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.4, minWidth: 0 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, bgcolor: "warning.main" }} />
            <Typography sx={{ flex: 1, minWidth: 0, fontSize: FS.TITLE, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {it.name || it.itemCd}
            </Typography>
            {it.readyToSellinDt && (
              <Typography sx={{ fontSize: FS.BODY, color: "text.secondary", flexShrink: 0 }}>
                {`${dateUtils.formatDate(it.readyToSellinDt, "MM-dd")} ${tt("homeNpiReadyDate")}`}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      {total > 0 && (
        <Typography sx={{ fontSize: FS.BODY, color: "text.secondary", mt: 0.5, flexShrink: 0 }}>
          {`${tt("homeNpiTotal")} ${total.toLocaleString()}${tt("homeCountUnit")}`}
        </Typography>
      )}
    </AppCard>
  );
};

/*
 * 계획 파이프라인 카드 — 각 단계의 "간단한 상태"만(상세 지표는 모듈 KPI 카드가 담당).
 *   판매실적: 최신 txn_date + 지난 완료 월 적재 여부 (sales-txn latest-date, 인덱스 MAX)
 *   수요계획: 계획 대상 거래처-품목 조합 수 (customer-item dp-target-count, COUNT 집계)
 *   나머지 모듈: 계획 대상 거점-품목 조합 수 (location-item, 모듈별 *_flg).
 * 버전 상태는 인사말 카드가 담당하므로 여기서 중복 표시하지 않는다.
 */
// '안전재고 설정' 카드 — IP 대상 조합 중 안전재고가 미설정인 것(safety_stock_qty·rp_policy_cd 둘 다 없음).
//   정책이 있으면 정책으로 산출되므로 미설정이 아니다. 클릭 → RP Policy 화면.
const SAFETY_STOCK_VIEW = "RP_POLICY";   // TabMenuList reduxKey

const SafetyStockCard = ({ tt, moduleKey }) => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    locationItemService.getSafetyStockMissing()
      .then((res) => setData(res?.data ?? null))
      .catch((e) => debug(e))
      .finally(() => setLoaded(true));
  }, []);

  const missingCount = data?.missingCount ?? 0;
  const missing = data?.missing ?? [];
  const open = useOpenView(SAFETY_STOCK_VIEW);
  useReportHomeSignal("safetyStock", () => (missingCount > 0
    ? { cardKey: "safetyStock", module: moduleKey, kind: "safetyStockMissing", severity: "warn", count: missingCount }
    : null), [missingCount, moduleKey]);

  return (
    <AppCard onClick={open} sx={{ p: 2, flex: 1, minWidth: 0, display: "flex", flexDirection: "column", ...drillCardSx(open) }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <CardIconBadge Icon={ShieldIcon} hex={moduleHex(moduleKey)} loading={!loaded} />
          <Typography className={CARD_TITLE_CLASS} sx={CARD_HEADER_SX}>{tt("homeSafetyStock")}</Typography>
        </Box>
        {missingCount > 0 && (
          <Typography sx={{ fontSize: FS.BODY, fontWeight: 600, flexShrink: 0, color: "warning.main", bgcolor: (th) => alpha(th.palette.warning.main, 0.14), borderRadius: 5, px: 1, py: 0.25 }}>
            {`${tt("homeSafetyStockMissing")} ${missingCount.toLocaleString()}${tt("homeCountUnit")}`}
          </Typography>
        )}
      </Box>
      <Typography sx={{ fontSize: FS.SUB, color: "text.secondary", mt: 0.25 }}>{tt("homeSafetyStockSubtitle")}</Typography>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", mt: 0.75 }}>
        {data && missingCount === 0 && (
          <Typography sx={{ fontSize: FS.SUB, color: "success.main" }}>{tt("homeSafetyStockAllSet")}</Typography>
        )}
        {missing.map((r) => (
          <Box key={`${r.locCd}-${r.itemCd}`} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.4, minWidth: 0 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, bgcolor: "warning.main" }} />
            <Typography sx={{ flex: 1, minWidth: 0, fontSize: FS.TITLE, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {r.itemName || r.itemCd}
            </Typography>
            <Typography sx={{ fontSize: FS.BODY, color: "text.secondary", flexShrink: 0 }}>{r.locCd}</Typography>
          </Box>
        ))}
      </Box>
    </AppCard>
  );
};

/*
 * 'BF 최근 실행' 카드 — z_bf_status 최신 1행(실행 상태 + 시각).
 *   조회 대상 BF 버전은 "인사말 카드가 보여주는 DP 버전"에 연결된 것(plan_config_obj BF_VERSION)을 쓴다.
 *   파이프라인 BF 단계는 활성 대상 조합 수를 보여주므로, 실행 상태는 이 카드가 담당한다.
 */
/*
  '예측 정확도' 카드 — 발행된 BF 예측이 실제 판매를 얼마나 맞췄나(DW z_fact_dp_demand).
    리더보드의 MAPE/WAPE 와는 다른 지표다 — 그건 모델 선정 근거(백테스트) 오차고, 이건 사후 정확도다.
    대상 월은 서버가 고른다("예측·실적이 둘 다 있는 가장 최근 완료 월") → 어느 월인지 칩으로 띄운다.
    그 월을 안 보여주면 사용자가 값을 해석할 수 없다(지난달인지 석 달 전인지 알 수 없으므로).
*/
/*
  '예측 정확도' 와 '계획 개선도' 가 같은 응답을 나눠 쓴다 — 한 달치 같은 행에서 나오는 값이라
  서버가 한 번의 스캔으로 둘 다 낸다.
  두 카드가 훅을 각각 부르지만 요청은 한 번만 나간다(useSharedDwRequest 의 참조 카운트).
*/
const useBfAccuracy = () => useSharedDwRequest("bfAccuracy");

const BfAccuracyCard = ({ tt, moduleKey }) => {
  const openPage = useCardDrill("bfAccuracy");
  const acc = useBfAccuracy();

  const pct = acc.data?.accuracyPct;
  const combos = acc.data?.comboCount;
  const month = acc.data?.targetMonth;
  const refreshed = acc.data?.refreshedTs;
  /*
    커버리지 — 그 달 계획 대상 중 실제로 잰 비율. 잰 조합 수만 보여주면 분모가 숨어서,
    1,245 개 중 898 개만 잰 값이 전체를 잰 값과 똑같이 읽힌다. 커버리지가 낮을수록 덜 믿어야 한다.
  */
  const total = acc.data?.totalComboCount;
  const coverage = combos > 0 && total > 0 ? Math.round((combos / total) * 100) : null;

  const value = !acc.loaded ? "—"
    : pct == null ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
      : `${Number(pct)}%`;

  return (
    <KpiCard
      leading={<ModuleIconBadge tt={tt} moduleKey={moduleKey} loading={!acc.loaded} />}
      label={tt("homeBfAccuracy")}
      onClick={openPage}
      value={value}
      sub={combos > 0
        ? [tt("homeBfAccuracyBasis"),
          `${combos.toLocaleString()}${tt("homeBfAccuracyCombos")}`,
          coverage != null ? `${coverage}% ${tt("homeBfAccuracyCoverage")}` : null].filter(Boolean).join(" · ")
        : tt("homeBfAccuracyBasis")}
      /* 칩엔 대상 기간(이 카드의 "기준" 은 곧 그 기간), 데이터가 만들어진 시각은 tooltip 으로. */
      right={targetPeriodChip(tt, month, acc.data?.periodStart, acc.data?.periodEnd, refreshed)}
    />
  );
};

/*
  '계획 대비 실적' 카드 — 매니저 계획(mf_qty) 대비 실적(sa_qty) 달성률(DW z_fact_dp_demand).
    100% 가 정상이고, 물량 가중이라 판매량 큰 조합이 값을 좌우한다.
    증감은 전월 달성률과의 차이(%p)다 — 비율의 비율이 아니라 포인트 차이라야 읽힌다.
*/
const PlanVsActualCard = ({ tt, moduleKey }) => {
  const openPage = useCardDrill("dpPlanVsActual");
  const [pva, setPva] = useState({ loaded: false, data: null });

  useEffect(() => {
    homeDwService.getPlanVsActual()
      .then((res) => setPva({ loaded: true, data: res?.data ?? null }))
      .catch((e) => { debug(e); setPva({ loaded: true, data: null }); });
  }, []);

  const pct = pva.data?.attainmentPct;
  const prev = pva.data?.prevAttainmentPct;
  const combos = pva.data?.comboCount;
  const total = pva.data?.totalComboCount;
  const month = pva.data?.month;
  const refreshed = pva.data?.refreshedTs;
  const coverage = combos > 0 && total > 0 ? Math.round((combos / total) * 100) : null;
  // %p 차이. 전월 값이 없으면(첫 달) 증감을 아예 표시하지 않는다 — 0 으로 두면 "변화 없음" 이 된다.
  const deltaPct = pct != null && prev != null ? Math.round((Number(pct) - Number(prev)) * 10) / 10 : null;

  const value = !pva.loaded ? "—"
    : pct == null ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
      : `${Number(pct)}%`;

  return (
    <KpiCard
      leading={<ModuleIconBadge tt={tt} moduleKey={moduleKey} loading={!pva.loaded} />}
      label={tt("homeDpPlanVsActual")}
      onClick={openPage}
      value={value}
      // 값 자체가 비율(%)이라 증감은 퍼센트포인트다 — "%" 로 붙이면 비율의 비율로 읽힌다.
      deltaPct={deltaPct}
      deltaUnit="pp"
      sub={combos > 0
        ? [tt("homePlanVsActualBasis"),
          `${combos.toLocaleString()}${tt("homeBfAccuracyCombos")}`,
          coverage != null ? `${coverage}% ${tt("homeBfAccuracyCoverage")}` : null].filter(Boolean).join(" · ")
        : tt("homePlanVsActualBasis")}
      right={targetPeriodChip(tt, month, pva.data?.periodStart, pva.data?.periodEnd, refreshed)}
    />
  );
};

/*
  IP 재고 카드 3종. 앞의 둘은 같은 스냅샷(z_fact_ip_inventory)이라 호출 하나를 나눠 쓰고,
  재고 금액만 실적 테이블(z_fact_inventory_actual)이라 따로 부른다.
    · 목표재고 달성률 — Σ현재고 / Σ목표재고, 100% 가 목표 수준(물량 가중)
    · 안전재고 하회   — 현재고 < 안전재고 인 조합 수. 0 건이 "다 채웠다" 인지 "잴 게 없었다" 인지
                        구분되도록 분모(safetyCheckedCount)를 함께 받아 표시한다
    · 재고 금액       — 최신 일자 실제 재고 금액(서버에서 base 통화로 환산)
*/
const useInventoryPlan = () => useSharedDwRequest("inventoryPlan");

/*
  IP 카드 칩 — 칩엔 **실적 일자**를 적는다. 이 카드들이 매일 움직이는 건 실적이 갱신되기 때문이고,
  사용자가 "언제 기준" 을 물을 때 알고 싶은 것도 그 날짜다.
  목표가 어느 계획 버전에서 왔는지는 tooltip 으로 — 중요하지만 매일 바뀌지 않는 정보다.
  (ETL 은 RELEASED 버전만 보므로 인사 카드가 보여주는 작업 중 버전과 다를 수 있다.)
*/
const inventoryChip = (tt, actualDate, planVersionName, planDate, refreshedTs) => (actualDate ? (
  <AsOfChip tt={tt} time={dateUtils.formatDate(actualDate, "yyyy-MM-dd")}
    tooltip={[
      planVersionName ? `${tt("homeIpTargetFrom")} ${planVersionName}${planDate ? ` (${dateUtils.formatDate(planDate, "yyyy-MM-dd")})` : ""}` : null,
      refreshedTs ? `${tt("homeDwRefreshed")} ${dateUtils.formatDate(refreshedTs, "yyyy-MM-dd HH:mm")}` : null,
    ].filter(Boolean).join(" · ") || null} />
) : null);

// 계획 대상 중 그날 실적이 있어 실제로 잰 비율. 낮으면 값의 대표성이 떨어진다.
const ipCoverage = (counted, planTotal) => (counted > 0 && planTotal > 0 ? Math.round((counted / planTotal) * 100) : null);

const IpTargetInvCard = ({ tt, moduleKey }) => {
  const openPage = useCardDrill("ipTargetInv");
  const ip = useInventoryPlan();
  const pct = ip.data?.attainmentPct;
  const combos = ip.data?.targetCount;
  const coverage = ipCoverage(combos, ip.data?.planComboCount);

  return (
    <KpiCard
      leading={<ModuleIconBadge tt={tt} moduleKey={moduleKey} loading={!ip.loaded} />}
      label={tt("homeIpTargetInv")}
      onClick={openPage}
      value={!ip.loaded ? "—" : pct == null ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box> : `${Number(pct)}%`}
      sub={combos > 0
        ? [tt("homeIpTargetInvBasis"), `${combos.toLocaleString()}${tt("homeIpCombos")}`,
          coverage != null ? `${coverage}% ${tt("homeBfAccuracyCoverage")}` : null].filter(Boolean).join(" · ")
        : tt("homeIpTargetInvBasis")}
      right={inventoryChip(tt, ip.data?.actualDate, ip.data?.planVersionName, ip.data?.planDate, ip.data?.refreshedTs)}
    />
  );
};

const IpSafetyStockCard = ({ tt, moduleKey }) => {
  const openPage = useCardDrill("ipSafetyStock");
  const ip = useInventoryPlan();
  const below = ip.data?.belowSafetyCount;
  const checked = ip.data?.safetyCheckedCount;
  useReportHomeSignal("ipSafetyStock", () => (below > 0
    ? { cardKey: "ipSafetyStock", module: moduleKey, kind: "ipBelowSafety", severity: "warn", count: below }
    : null), [below, moduleKey]);

  return (
    <KpiCard
      leading={<ModuleIconBadge tt={tt} moduleKey={moduleKey} loading={!ip.loaded} />}
      label={tt("homeIpSafetyStock")}
      onClick={openPage}
      value={!ip.loaded ? "—" : below == null ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
        : <Box component="span" sx={{ color: below > 0 ? "warning.main" : "text.primary" }}>{below.toLocaleString()}</Box>}
      sub={checked > 0 ? `${tt("homeIpSafetyBasis")} · ${checked.toLocaleString()}${tt("homeIpChecked")}` : tt("homeIpSafetyBasis")}
      right={inventoryChip(tt, ip.data?.actualDate, ip.data?.planVersionName, ip.data?.planDate, ip.data?.refreshedTs)}
    />
  );
};

const IpInventoryAmtCard = ({ tt, moduleKey }) => {
  const openPage = useCardDrill("ipInventoryAmt");
  const [inv, setInv] = useState({ loaded: false, data: null });

  useEffect(() => {
    homeDwService.getInventoryAmt()
      .then((res) => setInv({ loaded: true, data: res?.data ?? null }))
      .catch((e) => { debug(e); setInv({ loaded: true, data: null }); });
  }, []);

  const amt = inv.data?.invAmt;
  const items = inv.data?.itemCount;
  const currencyCd = inv.data?.currencyCd;
  // 통화가 여러 개면 환산이 개입한 값이다 — 그 사실을 부제에 드러낸다.
  const converted = (inv.data?.currencyCount ?? 0) > 1;

  return (
    <KpiCard
      leading={<ModuleIconBadge tt={tt} moduleKey={moduleKey} loading={!inv.loaded} />}
      label={tt("homeIpInventoryAmt")}
      onClick={openPage}
      // 금액은 통화기호와 함께, T/B/M/K 축약 — DW 대시보드 공용 포맷터를 그대로 쓴다.
      value={!inv.loaded || amt == null ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
        : makeFmtMoney(currencyCd)(Number(amt))}
      sub={[items > 0 ? `${items.toLocaleString()}${tt("homeIpItems")}` : null,
        converted ? tt("homeIpConverted") : null].filter(Boolean).join(" · ") || null}
      right={inventoryChip(tt, inv.data?.baseDate, null, null, null)}
    />
  );
};

/*
  RP · MP 카드 6종 — 전부 계획 fact 라 "앞으로 3개월" 을 본다(§14-1).
  구간은 서버가 ETL 적재 시점 기준으로 잡고 fromMonth/toMonth 로 돌려준다.
  칩엔 그 구간을 찍는다 — 계획이 오래됐으면 구간이 과거로 밀리는데, 값이 아니라 기간에서 드러나야 한다.
*/
const periodChip = (tt, fromMonth, toMonth, refreshedTs) => (fromMonth ? (
  <AsOfChip tt={tt} kind="target"
    time={`${dateUtils.formatDate(fromMonth, "yyyy-MM")}~${toMonth ? dateUtils.formatDate(new Date(new Date(toMonth).setMonth(new Date(toMonth).getMonth() - 1)), "MM") : ""}`}
    tooltip={refreshedTs ? `${tt("homeDwRefreshed")} ${dateUtils.formatDate(refreshedTs, "yyyy-MM-dd HH:mm")}` : null} />
) : null);

// RP 3종은 한 응답을 나눠 쓴다(같은 fact·같은 구간) — 세 카드가 불러도 요청은 1회.
const useRpSummary = () => useSharedDwRequest("rpSummary");

const RpFillRateCard = ({ tt, moduleKey }) => {
  const openPage = useCardDrill("rpFillRate");
  const rp = useRpSummary();
  const pct = rp.data?.fillRatePct;
  const rows = rp.data?.rowCount;

  return (
    <KpiCard
      leading={<ModuleIconBadge tt={tt} moduleKey={moduleKey} loading={!rp.loaded} />}
      label={tt("homeRpFillRate")}
      onClick={openPage}
      value={!rp.loaded || pct == null ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box> : `${Number(pct)}%`}
      sub={rows > 0 ? `${tt("homeRpFillRateBasis")} · ${rows.toLocaleString()}${tt("homeIpCombos")}` : tt("homeRpFillRateBasis")}
      right={periodChip(tt, rp.data?.fromMonth, rp.data?.toMonth, rp.data?.refreshedTs)}
    />
  );
};

const RpOpenExcCard = ({ tt, moduleKey }) => {
  const openPage = useCardDrill("rpOpenExc");
  const rp = useRpSummary();
  const count = rp.data?.openExcCount;
  const high = rp.data?.highCount ?? 0;
  const medium = rp.data?.mediumCount ?? 0;
  // 값 색상과 같은 규칙 — HIGH(재고 소진)가 있으면 error, 그 외 예외만 있으면 warn.
  useReportHomeSignal("rpOpenExc", () => (count > 0
    ? { cardKey: "rpOpenExc", module: moduleKey, kind: "rpOpenExc",
        severity: high > 0 ? "error" : "warn", count }
    : null), [count, high, moduleKey]);

  return (
    <KpiCard
      leading={<ModuleIconBadge tt={tt} moduleKey={moduleKey} loading={!rp.loaded} />}
      label={tt("homeRpOpenExc")}
      onClick={openPage}
      value={!rp.loaded || count == null ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
        : <Box component="span" sx={{ color: high > 0 ? "error.main" : count > 0 ? "warning.main" : "text.primary" }}>{count.toLocaleString()}</Box>}
      // HIGH(재고 소진)와 MEDIUM(리드타임보다 커버리지 짧음)은 무게가 다르다 — 합만 보여주면 구분이 사라진다.
      sub={count > 0 ? `${tt("homeRpExcHigh")} ${high.toLocaleString()} · ${tt("homeRpExcMedium")} ${medium.toLocaleString()}` : tt("homeRpExcNone")}
      right={periodChip(tt, rp.data?.fromMonth, rp.data?.toMonth, rp.data?.refreshedTs)}
    />
  );
};

const RpStockoutCard = ({ tt, moduleKey }) => {
  const openPage = useCardDrill("rpBackorder");
  const rp = useRpSummary();
  const qty = rp.data?.stockoutQty;
  const skus = rp.data?.stockoutSkuCount;
  // 부족 '수량'은 질문 문장에 넣지 않는다(단위가 품목마다 달라 숫자만으론 뜻이 없다) — SKU 수만 센다.
  useReportHomeSignal("rpBackorder", () => (qty > 0
    ? { cardKey: "rpBackorder", module: moduleKey, kind: "rpStockout", severity: "warn", count: skus ?? null }
    : null), [qty, skus, moduleKey]);

  return (
    <KpiCard
      leading={<ModuleIconBadge tt={tt} moduleKey={moduleKey} loading={!rp.loaded} />}
      label={tt("homeRpBackorder")}
      onClick={openPage}
      value={!rp.loaded || qty == null ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
        : <Box component="span" sx={{ color: qty > 0 ? "warning.main" : "text.primary" }}>{fmtQty(Number(qty))}</Box>}
      sub={skus > 0 ? `${tt("homeRpStockoutBasis")} · ${skus.toLocaleString()}${tt("homeRpSkus")}` : tt("homeRpStockoutBasis")}
      right={periodChip(tt, rp.data?.fromMonth, rp.data?.toMonth, rp.data?.refreshedTs)}
    />
  );
};

// MP 생산 2종도 한 응답을 나눠 쓴다.
const useMpProduction = () => useSharedDwRequest("mpProduction");

const MpPlanRateCard = ({ tt, moduleKey }) => {
  const openPage = useCardDrill("mpPlanRate");
  const mp = useMpProduction();
  const pct = mp.data?.planRatePct;
  // 수요와 생산이 서로 다른 행에 기록되는 fact 라(자원 그레인) 행 수를 하나로 합치지 않는다.
  const demandRows = mp.data?.demandRowCount;

  return (
    <KpiCard
      leading={<ModuleIconBadge tt={tt} moduleKey={moduleKey} loading={!mp.loaded} />}
      label={tt("homeMpPlanRate")}
      onClick={openPage}
      value={!mp.loaded || pct == null ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box> : `${Number(pct)}%`}
      sub={demandRows > 0 ? `${tt("homeMpPlanRateBasis")} · ${demandRows.toLocaleString()}${tt("homeMpDemandRows")}` : tt("homeMpPlanRateBasis")}
      right={periodChip(tt, mp.data?.fromMonth, mp.data?.toMonth, mp.data?.refreshedTs)}
    />
  );
};

const MpCapacityCard = ({ tt, moduleKey }) => {
  const openPage = useCardDrill("mpCapacity");
  const mp = useMpProduction();
  const pct = mp.data?.utilPct;
  const peak = mp.data?.peakUtilPct;
  const overCapa = pct != null && Number(pct) > 100;   // 값이 error 색으로 바뀌는 바로 그 조건
  useReportHomeSignal("mpCapacity", () => (overCapa
    ? { cardKey: "mpCapacity", module: moduleKey, kind: "mpOverCapacity", severity: "error", count: Number(pct) }
    : null), [overCapa, pct, moduleKey]);

  return (
    <KpiCard
      leading={<ModuleIconBadge tt={tt} moduleKey={moduleKey} loading={!mp.loaded} />}
      label={tt("homeMpCapacity")}
      onClick={openPage}
      value={!mp.loaded || pct == null ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
        : <Box component="span" sx={{ color: Number(pct) > 100 ? "error.main" : "text.primary" }}>{`${Number(pct)}%`}</Box>}
      // 총량 부하율은 평탄해 보여도 특정 자원·기간이 터질 수 있다 — 피크를 따로 적는다.
      sub={peak != null ? `${tt("homeMpPeakUtil")} ${Number(peak)}%` : tt("homeMpCapacityBasis")}
      right={periodChip(tt, mp.data?.fromMonth, mp.data?.toMonth, mp.data?.refreshedTs)}
    />
  );
};

const MpExceptionsCard = ({ tt, moduleKey }) => {
  const openPage = useCardDrill("mpExceptions");
  const [mo, setMo] = useState({ loaded: false, data: null });

  useEffect(() => {
    homeDwService.getMpOrder()
      .then((res) => setMo({ loaded: true, data: res?.data ?? null }))
      .catch((e) => { debug(e); setMo({ loaded: true, data: null }); });
  }, []);

  /*
    값은 지연 + 부족 합인데 이건 **주문 수가 아니라 건수**다. 한 주문이 지연이면서 부족일 수 있고
    (데모: 둘 다인 행 32개) 행 그레인에 주문 식별자가 없어 중복을 걷어낼 수 없다.
    그래서 카드 이름을 '지연·부족 건' 으로 두어 값의 단위를 이름이 말하게 한다 —
    '생산 예외' 라고 두면 제품의 Production Exceptions(미출하 수량, FG 한정)와 같은 걸로 읽힌다.
  */
  const count = mo.data?.exceptionCount;
  const late = mo.data?.lateCount ?? 0;
  const short = mo.data?.shortCount ?? 0;
  /*
    원인 4종은 **증상×원인 크로스탭**에서 온다(MpFactMapper 주석 참조) — 지연·부족인 주문만 센다.
    그래서 정상이면 원인 합 == 건수이고 '미분류' 는 0 이다.
    그래도 미분류 칸은 남긴다 — 원인 코드가 새로 생기면 그게 여기로 떨어져 바로 눈에 띈다.
      · data 를 빼먹으면 그만큼이 미분류로 표시된다. 분류돼 있는데 분류 안 됐다고 말하는 셈이라
        실제로 그 버그를 겪었다(mj: 미분류 2,294 로 보이던 것이 전부 DATA 였다).
  */
  const causeSum = (mo.data?.materialCause ?? 0) + (mo.data?.capacityCause ?? 0)
    + (mo.data?.dataCause ?? 0) + (mo.data?.otherCause ?? 0);
  const causes = [
    [tt("homeMpCauseMaterial"), mo.data?.materialCause],
    [tt("homeMpCauseCapacity"), mo.data?.capacityCause],
    [tt("homeMpCauseData"), mo.data?.dataCause],
    [tt("homeMpCauseOther"), mo.data?.otherCause],
    [tt("homeMpCauseUnknown"), count != null && count > causeSum ? count - causeSum : 0],
  ].filter(([, n]) => n > 0).map(([label, n]) => `${label} ${n.toLocaleString()}`);
  useReportHomeSignal("mpExceptions", () => (count > 0
    ? { cardKey: "mpExceptions", module: moduleKey, kind: "mpExceptions", severity: "warn", count }
    : null), [count, moduleKey]);

  return (
    <KpiCard
      leading={<ModuleIconBadge tt={tt} moduleKey={moduleKey} loading={!mo.loaded} />}
      label={tt("homeMpExceptions")}
      onClick={openPage}
      value={!mo.loaded || count == null ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
        : <Box component="span" sx={{ color: count > 0 ? "warning.main" : "text.primary" }}>{count.toLocaleString()}</Box>}
      sub={count > 0
        ? `${tt("homeMpLate")} ${late.toLocaleString()} · ${tt("homeMpShort")} ${short.toLocaleString()}${causes.length ? ` · ${causes.join(" · ")}` : ""}`
        : tt("homeMpExcNone")}
      right={periodChip(tt, mo.data?.fromMonth, mo.data?.toMonth, mo.data?.refreshedTs)}
    />
  );
};

/*
  '예측 편차 항목' + '판매추세 이상' — 둘 다 조합 단위 지표라 한 응답을 나눠 쓴다.
    편차: bf_wape > 0.3 인 조합 수. 리더보드가 아니라 DW fact 의 전파값을 쓴다(§10-4 의 벽을 피함).
    추세: 최근 3개월 평균 / 6개월 평균. 6개월이 3개월을 포함하므로 1.3 이면 최근이 확연히 높다는 뜻.
*/
const useDemandSignals = () => useSharedDwRequest("demandSignals");

const BfDeviationCard = ({ tt, moduleKey }) => {
  const openPage = useCardDrill("bfDeviation");
  const sig = useDemandSignals();
  const count = sig.data?.highDeviationCount;
  const checked = sig.data?.wapeComboCount;
  useReportHomeSignal("bfDeviation", () => (count > 0
    ? { cardKey: "bfDeviation", module: moduleKey, kind: "bfHighDeviation", severity: "warn", count }
    : null), [count, moduleKey]);

  return (
    <KpiCard
      leading={<ModuleIconBadge tt={tt} moduleKey={moduleKey} loading={!sig.loaded} />}
      label={tt("homeBfDeviation")}
      onClick={openPage}
      value={!sig.loaded || count == null ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
        : <Box component="span" sx={{ color: count > 0 ? "warning.main" : "text.primary" }}>{count.toLocaleString()}</Box>}
      // 분모를 함께 — 0 건이 "다 좋다" 인지 "잴 게 없었다" 인지 구분되어야 한다.
      sub={checked > 0 ? `${tt("homeBfDeviationBasis")} · ${checked.toLocaleString()}${tt("homeIpCombos")}` : tt("homeBfDeviationBasis")}
      right={sig.data?.refreshedTs ? <AsOfChip tt={tt} kind="loaded" time={dateUtils.formatDate(sig.data.refreshedTs, "yyyy-MM-dd")} /> : null}
    />
  );
};

const DpSalesTrendCard = ({ tt, moduleKey }) => {
  const openPage = useCardDrill("dpSalesTrend");
  const sig = useDemandSignals();
  const surge = sig.data?.surgeCount;
  const drop = sig.data?.dropCount;
  const checked = sig.data?.trendComboCount;
  const total = surge != null && drop != null ? surge + drop : null;
  useReportHomeSignal("dpSalesTrend", () => (total > 0
    ? { cardKey: "dpSalesTrend", module: moduleKey, kind: "dpTrendAnomaly", severity: "warn", count: total }
    : null), [total, moduleKey]);

  return (
    <KpiCard
      leading={<ModuleIconBadge tt={tt} moduleKey={moduleKey} loading={!sig.loaded} />}
      label={tt("homeDpSalesTrend")}
      onClick={openPage}
      value={!sig.loaded || total == null ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
        : <Box component="span" sx={{ color: total > 0 ? "warning.main" : "text.primary" }}>{total.toLocaleString()}</Box>}
      // 급증과 급감은 방향이 반대라 합만 보여주면 무엇이 일어났는지 알 수 없다.
      sub={total > 0
        ? `${tt("homeDpSurge")} ${surge.toLocaleString()} · ${tt("homeDpDrop")} ${drop.toLocaleString()}`
        : (checked > 0 ? `${tt("homeDpTrendBasis")} · ${checked.toLocaleString()}${tt("homeIpCombos")}` : tt("homeDpTrendBasis"))}
      right={sig.data?.refreshedTs ? <AsOfChip tt={tt} kind="loaded" time={dateUtils.formatDate(sig.data.refreshedTs, "yyyy-MM-dd")} /> : null}
    />
  );
};

const BF_RUN_STATUS = {
  COMPLETED: { key: "homeStatusNormal", color: "success.main" },
  PROCESSING: { key: "homeBadgeBfRunning", color: "primary.main" },
  ERROR: { key: "homeBadgeBfFailed", color: "error.main" },
};

/*
  BF 실행 단계 — z_bf_status.seq_num. 100 은 첫 단계가 아니라 **실행 전체의 마스터 행**이고
  (DpProcessMgmtController/DpStatusDashBoardService 가 seq 100 으로 완료를 판정한다),
  110~170 이 실제 진행 단계다. 진행률은 110 을 1로 세어 7단계로 표시한다.
*/
const BF_STEPS = [110, 120, 130, 140, 150, 160, 170];
const BF_STEP_KEY = {
  100: "homeBfStep100", 110: "homeBfStep110", 120: "homeBfStep120", 130: "homeBfStep130",
  140: "homeBfStep140", 150: "homeBfStep150", 160: "homeBfStep160", 170: "homeBfStep170",
};

/*
  단계 행들로 실행 상태를 판정한다. 최신 1행으로는 안 되는 이유 —
  단계 사이(130 COMPLETED 기록, 140 아직 미기록)에는 최신 행이 COMPLETED 라 완료로 보인다.
    ① 마스터(seq 100)가 COMPLETED  → 실행 완료
    ② ERROR 단계가 있으면          → 그 단계에서 중단
    ③ 그 외 행이 있으면            → 실행 중(가장 앞선 단계를 현재 단계로)
*/
const resolveBfRun = (steps = []) => {
  if (!steps.length) return { statusCd: null, step: null };

  const master = steps.find((s) => s.seqNum === 100);
  if (master?.statusCd === "COMPLETED") {
    return { statusCd: "COMPLETED", step: null, ts: master.endTs ?? master.startTs };
  }

  const failed = steps.find((s) => s.statusCd === "ERROR");
  if (failed) return { statusCd: "ERROR", step: failed.seqNum, ts: failed.endTs ?? failed.startTs };

  // 진행 중 단계가 있으면 그것, 없으면 기록된 마지막 단계(다음 단계 기록 대기 중)
  const running = steps.filter((s) => s.statusCd === "PROCESSING" && s.seqNum !== 100);
  const current = running.length ? Math.max(...running.map((s) => s.seqNum))
    : Math.max(...steps.map((s) => s.seqNum));
  const currentRow = steps.find((s) => s.seqNum === current);
  return { statusCd: "PROCESSING", step: current, ts: currentRow?.startTs ?? master?.startTs };
};

const BfRunStatusCard = ({ tt, moduleKey, versions = [] }) => {
  const openPage = useCardDrill("bfRunStatus");
  const [bf, setBf] = useState({ loaded: false, data: null });
  const dpVersionId = versions.find((v) => v.moduleCd === "DP")?.id ?? null;

  useEffect(() => {
    if (dpVersionId == null) { setBf({ loaded: true, data: null }); return; }
    dpProcessMgmtService.getBfLatestStatus(dpVersionId)
      .then((res) => setBf({ loaded: true, data: res?.data ?? null }))
      .catch((e) => { debug(e); setBf({ loaded: true, data: null }); });
  }, [dpVersionId]);

  const run = resolveBfRun(bf.data?.steps);
  const status = BF_RUN_STATUS[run.statusCd];
  const value = !bf.loaded ? "—" : tt(status?.key ?? "homeBadgeBfNotRun");
  // 실패(ERROR)는 지금 막힌 것, 미실행(statusCd 없음)은 시작 전 확인거리. 진행중은 신고하지 않는다.
  const bfKind = !bf.loaded ? null
    : run.statusCd === "ERROR" ? "bfRunFailed"
      : run.statusCd == null ? "bfRunNotRun" : null;
  useReportHomeSignal("bfRunStatus", () => (bfKind
    ? { cardKey: "bfRunStatus", module: moduleKey, kind: bfKind,
        severity: bfKind === "bfRunFailed" ? "error" : "warn" }
    : null), [bfKind, moduleKey]);
  /*
    칩 = <BF 버전명> · <실행 시각> — IP 카드(§12)와 같은 자리, 같은 형식이다.
      "어느 것을 잰 값인가" 는 카드마다 칩에서 읽히는 편이 낫다. 조회 시각을 쓰던 것을 실행 시각으로
      바꾼 이유도 같다 — 이 카드에서 데이터의 시점은 내가 물어본 때가 아니라 그 실행이 끝난 때다.
  */
  const verName = bf.data?.bfVersionName;
  const tsText = run.ts ? dateUtils.formatDate(run.ts, "yyyy-MM-dd HH:mm") : null;
  const chipText = [verName, tsText].filter(Boolean).join(" · ") || null;
  /*
    부제는 지금 무슨 일이 일어나는지만 — 실행 중엔 "언제" 보다 "어디까지" 가 궁금하고,
    실패는 어느 단계에서 멎었는지가 곧 다음 행동이다. 완료면 칩의 시각으로 충분해 부제를 비운다.
  */
  const stepLabel = run.step != null ? tt(BF_STEP_KEY[run.step]) : null;
  const sub = run.statusCd === "PROCESSING" && stepLabel
    ? `${stepLabel}${BF_STEPS.includes(run.step) ? ` (${BF_STEPS.indexOf(run.step) + 1}/${BF_STEPS.length})` : ""}`
    : run.statusCd === "ERROR" && stepLabel ? `${stepLabel} ${tt("homeBfStepFailed")}`
      : null;

  return (
    <KpiCard
      leading={<ModuleIconBadge tt={tt} moduleKey={moduleKey} loading={!bf.loaded} />}
      label={tt("homeBfRunStatus")}
      onClick={openPage}
      value={<Box component="span" sx={{ color: status?.color ?? "text.disabled" }}>{value}</Box>}
      sub={sub}
      right={chipText ? <AsOfChip tt={tt} time={chipText} /> : null}
    />
  );
};

// 모듈별 "계획 대상 조합 수" 조회기 — 카드에 뜨는 모듈은 전부 여기 등록돼 있어야 한다.
//   DP/BF=거래처-품목(z_customer_item 의 dp_flg/bf_flg), IP/RP/MP=거점-품목(z_location_item 의 ip_flg/rp_flg/mp_flg).
const TARGET_COUNT_FETCHER = {
  BF: () => customerItemService.getBfTargetCount(),
  DP: () => customerItemService.getDpTargetCount(),
  IP: () => locationItemService.getIpTargetCount(),
  RP: () => locationItemService.getRpTargetCount(),
  MP: () => locationItemService.getMpTargetCount(),
};

const PipelineCard = ({ tt, permitted, compact = false }) => {
  const [sales, setSales] = useState({ loaded: false, latest: null });
  // 모듈별 조합 수 — { DP: { activeCount, inactiveCount }, ... }. 권한 있는 모듈만 조회한다.
  const [targets, setTargets] = useState({});

  const permittedKey = permitted.map((m) => m.key).join(",");
  useEffect(() => {
    const keys = permittedKey ? permittedKey.split(",") : [];
    Object.entries(TARGET_COUNT_FETCHER).forEach(([mod, fetcher]) => {
      if (!keys.includes(mod)) return;
      fetcher()
        .then((res) => setTargets((prev) => ({ ...prev, [mod]: res?.data ?? null })))
        // 실패해도 null 을 넣어 '조회 끝' 을 남긴다 — 안 그러면 undefined 로 남아 로딩 링이 안 멈춘다.
        .catch((e) => { debug(e); setTargets((prev) => ({ ...prev, [mod]: null })); });
    });
  }, [permittedKey]);

  useEffect(() => {
    // 전용 경량 엔드포인트 — SELECT MAX(txn_date) 만 수행(txn_date 인덱스 backward scan 1건)이라 랜딩에서 안전.
    //   Data Transform 의 /api/data-transform 은 전체 source/target(무거운 z_dp_cube measure 쿼리 포함)을 계산하므로 홈에는 쓰지 않는다.
    salesTxnService.getLatestDate()
      .then((res) => setSales({ loaded: true, latest: res?.data?.latestDate ?? null }))
      .catch((e) => { debug(e); setSales({ loaded: true, latest: null }); });
  }, []);

  // 단계마다 호출이 따로 끝나는 카드라, 하나라도 안 온 동안 헤더 배지에 로딩 링을 둔다.
  const pipelineLoading = !sales.loaded || permitted.some((m) => targets[m.key] === undefined);

  // A 기준: 최신 실판매 일자가 지난 완료 월 시작일 이상이면 정상(= 지난달까지 적재됨), 아니면 적재 필요.
  const now = new Date();
  const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const latestDate = sales.latest ? new Date(sales.latest) : null;
  const salesBadge = !sales.loaded ? null
    : (latestDate && latestDate >= prevMonthStart)
      ? { variant: "ok", key: "homeStatusNormal" }
      : { variant: "warn", key: "homeBadgeLoadNeeded" };
  const salesDateText = latestDate ? dateUtils.formatDate(latestDate, "yyyy-MM-dd") : null;
  // AI 어시스턴트 카드에 신고 — 배지 판정(salesBadge)을 그대로 옮긴다.
  const salesWarn = salesBadge?.variant === "warn";
  useReportHomeSignal("pipeline.sales", () => (salesWarn
    ? { cardKey: "pipeline", module: null, kind: "salesNotLoaded", severity: "warn" }
    : null), [salesWarn]);

  // 계획 대상 조합 수를 가진 모듈(DP/IP)은 실데이터, 나머지는 아직 목업.
  //   조합이 0이면 계획 대상이 없다는 뜻이라 확인 필요(warn), 있으면 정상.
  //   활성 조합이 0이면 계획 대상이 없다는 뜻이라 확인 필요(warn), 있으면 정상.
  //   비활성(조합·거점/거래처 active_flg='N')은 있으면 함께 표기해 계획 범위 밖 규모를 보여준다.
  const targetStage = (m) => {
    const c = targets[m.key];
    const active = c?.activeCount;
    const inactive = c?.inactiveCount ?? 0;
    const badge = active == null ? null
      : active > 0 ? { variant: "ok", key: "homeStatusNormal" } : { variant: "warn", key: "homeBadgeNoTarget" };
    // 비활성 0 이면 "-" — 항상 "활성 / 비활성" 쌍으로 보여 자리가 흔들리지 않게 한다.
    const extra = active == null ? null
      : `${active.toLocaleString()} / ${inactive > 0 ? inactive.toLocaleString() : "-"}`;
    return { key: m.key, nameKey: m.nameKey, badge, extra };
  };
  /*
    활성 대상이 0인 모듈 — 목록 순서상 첫 번째만 신고한다.
    질문 목록은 **카드당 1건** 상한이라 여러 건을 실어도 어차피 하나만 질문이 되고,
    permitted 순서가 고정이라 판정도 결정형이다(activeCount 가 아직 안 온 모듈은 null 이라 제외).
  */
  const noTargetMod = permitted.find((m) => targets[m.key]?.activeCount === 0) ?? null;
  useReportHomeSignal("pipeline.target", () => (noTargetMod
    ? { cardKey: "pipeline", module: noTargetMod.key, kind: "noActiveTarget",
        severity: "warn", labelKey: noTargetMod.nameKey }
    : null), [noTargetMod?.key]);
  const stages = [
    { key: "salesActual", nameKey: "homeStageSalesActual", badge: salesBadge, extra: salesDateText },
    ...permitted.map((m) => {
      // BF/DP/IP/RP/MP 모두 "활성 대상 조합 수" — BF 실행 상태는 전용 카드(BfRunStatusCard)가 담당한다.
      return targetStage(m);
    }),
  ];

  return (
    <AppCard sx={{ p: 2, flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <CardIconBadge Icon={FactCheckIcon} hex={coreHex()} loading={pipelineLoading} />
        <Typography sx={CARD_HEADER_SX}>{tt("homePipeline")}</Typography>
      </Box>
      {/* 좁은 폭(1열)에서는 설명·힌트를 접고 이름+배지만 — 단계가 6개라 그대로 두면 넘친다. */}
      {!compact && <Typography sx={{ fontSize: FS.SUB, color: "text.secondary", mb: 1 }}>{tt("homePipelineHint")}</Typography>}
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, ...(compact && { overflow: "auto", mt: 0.5 }) }}>
        {stages.map((s, idx) => {
          const hex = moduleHex(s.key);
          const Icon = STAGE_ICON[s.key];
          return (
            <Box key={s.key} sx={{
              display: "flex", alignItems: "center", gap: compact ? 0.75 : 1.25, minWidth: 0,
              ...(compact ? { py: 0.3, flexShrink: 0 } : { flex: 1 }),
              borderTop: idx === 0 ? 0 : 1, borderColor: "divider",
            }}>
              {Icon && <Icon sx={{ fontSize: compact ? 15 : 18, color: hex || "text.secondary", flexShrink: 0 }} />}
              <Typography sx={{ fontSize: compact ? FS.SUB : FS.H2, fontWeight: 500, color: "text.primary", ...(compact ? { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } : { flexShrink: 0 }) }}>
                {tt(s.nameKey)}
              </Typography>
              {!compact && (
                <Typography sx={{ flex: 1, minWidth: 0, ml: 0.5, fontSize: FS.SUB, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {tt(STAGE_DESC_KEY[s.key])}
                </Typography>
              )}
              {s.extra && <Typography sx={{ fontSize: compact ? FS.BODY : FS.SUB, color: "text.secondary", flexShrink: 0, mr: 0.5 }}>{s.extra}</Typography>}
              {/* 정상은 배지를 숨긴다 — 배지가 보이면 곧 확인할 일이라는 뜻(항상 "정상"이면 신호가 죽는다) */}
              {s.badge && s.badge.variant !== "ok" && <StageBadge tt={tt} badge={s.badge} compact={compact} />}
            </Box>
          );
        })}
      </Box>
    </AppCard>
  );
};

/*
  '수요계획 확정' — 확정(RELEASED)된 DP 버전이 무엇인지.
  인사말 칩과 일부러 다른 값을 보여준다 — 칩은 "지금 작업 중인 버전", 이 카드는 "확정된 버전"이다.
  둘이 갈릴 때(작업 중 버전이 따로 있을 때)만 부제에 그걸 덧붙인다. 확정본만 보면 이번 사이클
  작업이 시작됐는지 알 수 없고, 늘 붙이면 갈리지 않은 평상시엔 군더더기가 된다.
  버전을 따로 부르지 않는다 — 인사말 칩이 이미 받아 온 DP 행을 재사용한다(ctx.dpVer).
*/
const DpVersionCard = ({ tt, moduleKey, loaded, released, wip }) => {
  const openPage = useCardDrill("dpVersion");
  return (
  <KpiCard
    leading={<ModuleIconBadge tt={tt} moduleKey={moduleKey} loading={!loaded} />}
    label={tt("homeDpVersion")}
    onClick={openPage}
    // 버전명은 숫자가 아니라 식별자다 — KPI 기본 24px 로는 1열 카드에서 넘친다.
    value={!loaded || !released
      ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
      : <Box component="span" sx={{ fontSize: FS.DISPLAY }}>{released.name}</Box>}
    sub={!loaded ? "" : (released
      ? `${tt("homeDpReleasedOn")} ${dateUtils.formatDate(released.releaseTs, "yyyy-MM-dd")}${wip ? ` · ${tt("homeDpInProgress")} ${wip.name}` : ""}`
      : tt("homeDpNoReleased"))}
  />
  );
};

/*
  '계획 개선도' — 매니저 계획(MF)이 BF 예측보다 실제 판매를 더 잘 맞췄나.
    개선도 = (BF오차 − MF오차) / BF오차 × 100.  + 면 사람이 손댄 게 값을 더한 것, − 면 깎아먹은 것.

  왜 이 카드인가: '예측 정확도' 는 BF 만, '계획 대비 실적' 은 달성률만 본다. 둘 다
  "사람이 개입해서 나아졌나" 라는 S&OP 의 핵심 질문엔 답하지 않는다.

  절대오차 합으로 비교한다(행별 오차율 평균이 아니라) — 수량이 작은 조합의 오차율은 쉽게 폭주해서,
  평균을 내면 매출 대부분을 차지하는 큰 조합이 묻힌다.

  0 근처는 굳이 색칠하지 않는다. 계획과 예측이 사실상 같은 달에 ±1% 를 개선/악화로 읽으면
  매달 무의미하게 색이 바뀐다.
*/
const PLAN_LIFT_FLAT_PCT = 1;

const DpPlanLiftCard = ({ tt, moduleKey }) => {
  const openPage = useCardDrill("dpPlanLift");
  const acc = useBfAccuracy();
  const mfErr = acc.data?.mfAbsErr;
  const bfErr = acc.data?.bfAbsErr;
  const combos = acc.data?.planLiftComboCount;
  const month = acc.data?.targetMonth;

  // BF 오차가 0 이면(예측이 완벽했던 달) 개선도를 정의할 수 없다 — 분모가 없다.
  const lift = mfErr != null && bfErr > 0 ? Math.round(((bfErr - mfErr) / bfErr) * 1000) / 10 : null;
  const flat = lift != null && Math.abs(lift) < PLAN_LIFT_FLAT_PCT;
  const color = lift == null || flat ? "text.primary" : (lift > 0 ? "success.main" : "warning.main");

  useReportHomeSignal("dpPlanLift",
    () => (lift != null && lift <= -PLAN_LIFT_FLAT_PCT
      ? { cardKey: "dpPlanLift", module: moduleKey, kind: "dpPlanWorseThanForecast", severity: "warn", count: Math.abs(lift) }
      : null),
    [lift]);

  return (
    <KpiCard
      leading={<ModuleIconBadge tt={tt} moduleKey={moduleKey} loading={!acc.loaded} />}
      label={tt("homeDpPlanLift")}
      onClick={openPage}
      value={!acc.loaded || lift == null
        ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
        : <Box component="span" sx={{ color }}>{`${lift > 0 ? "+" : ""}${lift}%`}</Box>}
      sub={lift == null
        ? tt("homeDpPlanLiftBasis")
        : [tt(flat ? "homeDpPlanLiftFlat" : lift > 0 ? "homeDpPlanLiftBetter" : "homeDpPlanLiftWorse"),
          combos > 0 ? `${combos.toLocaleString()}${tt("homeBfAccuracyCombos")}` : null].filter(Boolean).join(" · ")}
      right={targetPeriodChip(tt, month, acc.data?.periodStart, acc.data?.periodEnd, acc.data?.refreshedTs)}
    />
  );
};

const CARD_CATALOG = [
  // ── core ──────────────────────────────────────────────
  {
    key: "greeting", module: "core", span: 2,
    render: ({ tt, ttv, username, today, versions, versionsLoading, onVersionClick }) => {
      // 시간대별 인사말 — 아침 <12 / 오후 12~17 / 저녁 18~
      const hr = new Date().getHours();
      const greetKey = hr < 12 ? "homeGreetingMorning" : hr < 18 ? "homeGreetingAfternoon" : "homeGreetingEvening";
      const noneSx = { color: "text.secondary", bgcolor: (th) => alpha(th.palette.text.primary, 0.06), border: "1px solid", borderColor: "divider" };
      const chipBaseSx = { display: "inline-block", flexShrink: 0, whiteSpace: "nowrap", fontSize: FS.SUB, fontWeight: 600, borderRadius: 5, px: 1.25, py: 0.5 };
      return (
        <AppCard sx={{ p: 2, flex: 1, minWidth: 0, position: "relative", display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CardIconBadge Icon={WavingHandIcon} hex={coreHex()} size={30} loading={versionsLoading} />
            <Typography sx={{ fontSize: FS.DISPLAY, fontWeight: 700, color: "text.primary" }}>{username ? `${tt(greetKey)}, ${username}` : tt(greetKey)}</Typography>
          </Box>
          <Typography sx={{ fontSize: FS.TITLE, color: "text.secondary", mt: 0.5 }}>{today}<GreetingAlertCount ttv={ttv} /></Typography>
          {/* 계획버전 칩: 권한 모듈별 대표 버전 — 모듈명 + 버전명 + 상태. 상태색은 VersionMgmt 와 동일 유틸. */}
          {/*
            카드 높이는 그리드가 150px 로 고정한다(gridAutoRows). 창이 좁아지면 칩이 한 줄에 하나씩
            떨어져 4줄이 되는데, 그대로 두면 아래 칩이 **말없이 잘린다**(RP·MP 가 안 보인다는 신고).

            바깥 Box = 스크롤 상자, 안쪽 Box = 줄바꿈 목록으로 **반드시 두 겹**이어야 한다.
              한 겹으로 두고 `flex-wrap: wrap` + `overflow: auto` 를 같이 주면 넘친 flex 줄이
              scrollHeight 에 안 잡혀 스크롤이 생기지 않는다(실측: clientH=scrollH=63 인데 칩은 잘림).
            바깥은 mt:auto 로 평소처럼 카드 아래에 붙어 있다가, 자리가 모자라면
            minHeight:0 덕분에 줄어들면서 스크롤이 생긴다 — 넓은 화면의 모습은 그대로다.
            높이가 늘 부족하면 카드 크기를 2×2 로 키울 수 있다(홈 카드 설정).
          */}
          <Box sx={{ mt: "auto", pt: 1, minHeight: 0, overflowY: "auto" }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {versions?.length ? versions.map((v) => {
              const modLabel = tt(MODULES.find((m) => m.key === v.moduleCd)?.nameKey ?? "");
              // 버전이 없는 모듈 — 회색 칩으로 "버전 없음" 을 명시(클릭 비활성)
              if (!v.name) {
                return (
                  <Typography key={v.moduleCd} component="span" sx={{ ...chipBaseSx, ...noneSx }}>
                    {`${modLabel} · ${tt("homeNoVersion")}`}
                  </Typography>
                );
              }
              const statusLabel = CodeUtil.versionStatusLabel(v.statusCd);
              return (
                <Typography key={v.moduleCd} component="span" onClick={() => onVersionClick?.(v)}
                  sx={{ ...chipBaseSx, ...ColorUtil.tintChipSx(ColorUtil.versionStatusColor(v.statusCd)),
                        cursor: "pointer", transition: "box-shadow .12s, filter .12s", "&:hover": { boxShadow: 2, filter: "brightness(0.97)" } }}>
                  {`${modLabel} ${v.name} · ${statusLabel}`}
                </Typography>
              );
            }) : (
              <Typography component="span" sx={{ ...chipBaseSx, ...noneSx }}>{tt("homeNoVersion")}</Typography>
            )}
          </Box>
          </Box>
        </AppCard>
      );
    },
  },
  {
    /*
      인사말 옆(첫 줄 오른쪽) — 묻고 시작하는 자리라 위에 둔다. 순서·크기는 사용자가 바꿀 수 있다.
      2행이 최소다: 시작 화면이 좌우 반반(최근 대화 / 업무 시작 질문)이고, 대화로 전환되면
      말풍선이 들어가야 한다. 1행으로는 둘 다 못 담는다.
    */
    key: "aiPrompt", module: "AI", role: ROLE.AI, span: 2, rowSpan: 2,
    // aiStack — 신문 지면만 true(좁고 높은 구역이라 좌우 2단이 눌린다). 벤토는 종전 그대로.
    render: ({ tt, ttv, size, aiStack }) => <HomeAiCard tt={tt} ttv={ttv} hex={moduleHex("AI")} size={size} stack={aiStack} />,
  },
  {
    key: "notifications", module: "core", span: 2,
    render: ({ tt }) => <NotificationsCard tt={tt} />,
  },
  {
    key: "pipeline", module: "core", span: 1, rowSpan: 2,
    // 높이 1행이면 6단계가 세로로 안 들어가므로 컴팩트, 2행 이상이면 설명·부가값까지 전부 표시.
    render: (ctx) => <PipelineCard tt={ctx.tt} permitted={ctx.permitted} compact={(ctx.size?.rowSpan ?? 1) < 2} />,
  },
  {
    key: "approvals", module: "DP", role: ROLE.DP, span: 1, rowSpan: 2,
    render: ({ tt, approvalAcOn, moduleKey }) => <ApprovalsCard tt={tt} enabled={approvalAcOn} moduleKey={moduleKey} />,
  },
  // 신제품(NPI) — z_item.npi_flg 기준이라 DP 모듈 소속.
  {
    key: "npi", module: "DP", role: ROLE.DP, span: 1, rowSpan: 2,
    render: ({ tt, moduleKey }) => <NpiCard tt={tt} moduleKey={moduleKey} />,
  },
  // 안전재고 설정 — z_location_item 기준이라 IP 모듈 소속.
  {
    key: "safetyStock", module: "IP", role: ROLE.IP, span: 1, rowSpan: 2,
    render: ({ tt, moduleKey }) => <SafetyStockCard tt={tt} moduleKey={moduleKey} />,
  },
  {
    key: "myTasks", module: "core", span: 1, rowSpan: 2,
    render: (ctx) => <MyTasksCard tt={ctx.tt} tall={(ctx.size?.rowSpan ?? 1) >= 2} />,
  },
  {
    key: "pinned", module: "core", span: 1, rowSpan: 2,
    render: ({ tt }) => <PinnedCard tt={tt} />,
  },
  // 회의 안건 — 모듈과 무관한 협업 화면이라 core. 다만 메뉴가 S&OP 아래라 그 그룹의
  //   역할 게이트를 따른다(view) — 화면을 못 여는 사용자에게 카드만 보여 주지 않는다.
  {
    key: "meetingAgenda", module: "core", view: "MEETING_AGENDA", span: 1, rowSpan: 2,
    render: ({ tt }) => <MeetingAgendaCard tt={tt} />,
  },

  // ── BF ────────────────────────────────────────────────
  { key: "bfAccuracy", module: "BF", role: ROLE.BF, span: 1, render: (ctx) => <BfAccuracyCard tt={ctx.tt} moduleKey="BF" /> },
  { key: "bfRunStatus", module: "BF", role: ROLE.BF, span: 1, render: (ctx) => <BfRunStatusCard tt={ctx.tt} moduleKey="BF" versions={ctx.versions} /> },
  { key: "bfDeviation", module: "BF", role: ROLE.BF, span: 1, render: (ctx) => <BfDeviationCard tt={ctx.tt} moduleKey="BF" /> },

  // ── DP ────────────────────────────────────────────────
  { key: "dpPlanLift", module: "DP", role: ROLE.DP, span: 1, render: (ctx) => <DpPlanLiftCard tt={ctx.tt} moduleKey="DP" /> },
  { key: "dpSalesTrend", module: "DP", role: ROLE.DP, span: 1, render: (ctx) => <DpSalesTrendCard tt={ctx.tt} moduleKey="DP" /> },
  { key: "dpPlanVsActual", module: "DP", role: ROLE.DP, span: 1, render: (ctx) => <PlanVsActualCard tt={ctx.tt} moduleKey="DP" /> },
  { key: "dpVersion", module: "DP", role: ROLE.DP, span: 1,
    render: (ctx) => <DpVersionCard tt={ctx.tt} moduleKey="DP" loaded={!ctx.versionsLoading} released={ctx.dpVer?.released} wip={ctx.dpVer?.wip} /> },

  // ── IP ────────────────────────────────────────────────
  { key: "ipTargetInv", module: "IP", role: ROLE.IP, span: 1, render: (ctx) => <IpTargetInvCard tt={ctx.tt} moduleKey="IP" /> },
  { key: "ipInventoryAmt", module: "IP", role: ROLE.IP, span: 1, render: (ctx) => <IpInventoryAmtCard tt={ctx.tt} moduleKey="IP" /> },
  { key: "ipSafetyStock", module: "IP", role: ROLE.IP, span: 1, render: (ctx) => <IpSafetyStockCard tt={ctx.tt} moduleKey="IP" /> },

  // ── RP ────────────────────────────────────────────────
  { key: "rpFillRate", module: "RP", role: ROLE.RP, span: 1, render: (ctx) => <RpFillRateCard tt={ctx.tt} moduleKey="RP" /> },
  { key: "rpOpenExc", module: "RP", role: ROLE.RP, span: 1, render: (ctx) => <RpOpenExcCard tt={ctx.tt} moduleKey="RP" /> },
  { key: "rpBackorder", module: "RP", role: ROLE.RP, span: 1, render: (ctx) => <RpStockoutCard tt={ctx.tt} moduleKey="RP" /> },

  // ── MP ────────────────────────────────────────────────
  { key: "mpPlanRate", module: "MP", role: ROLE.MP, span: 1, render: (ctx) => <MpPlanRateCard tt={ctx.tt} moduleKey="MP" /> },
  { key: "mpExceptions", module: "MP", role: ROLE.MP, span: 1, render: (ctx) => <MpExceptionsCard tt={ctx.tt} moduleKey="MP" /> },
  { key: "mpCapacity", module: "MP", role: ROLE.MP, span: 1, render: (ctx) => <MpCapacityCard tt={ctx.tt} moduleKey="MP" /> },
];

/*
  모듈 대표 버전 고르기 — 작업 중인 버전이 있으면 그것, 없으면 최신 릴리즈.
  앱의 다른 곳(PlanVersionService.getDpRecentVersion)과 같은 규칙이다. 홈만 다른 버전을 가리키면
  칩을 눌러 넘어간 화면과 어긋난다.

  updatedTs 로 고르던 걸 버렸다. 두 가지가 겹쳐 있었다:
    · updatedTs 는 릴리즈와 무관하게 바뀐다(설명 수정·상태 리셋 등) — "현재 버전" 의 지표가 아니다.
    · 한 번에 적재된 데이터(시드·마이그레이션)에선 전 행이 같은 값이라 **완전 동점**이 되고,
      그러면 DB 가 주는 임의 순서의 첫 행이 뽑힌다. 실제로 모듈마다 최신도 아닌 버전이 떴다.
  그래서 정렬 키가 같을 때 id 로 한 번 더 가른다 — 안 그러면 새로고침마다 다른 버전이 뜰 수 있다.
*/
const WIP_STATUS = [CodeUtil.versionStatusCode.SCENARIO, CodeUtil.versionStatusCode.PLANNED, CodeUtil.versionStatusCode.UNPLANNED];
const verTs = (v) => {
  const t = v ? new Date(v).getTime() : 0;      // ISO 문자열이든 Date 든 같은 방식으로 비교
  return Number.isNaN(t) ? 0 : t;
};
const byTsDesc = (key) => (a, b) => verTs(b[key]) - verTs(a[key]) || (Number(b.id) || 0) - (Number(a.id) || 0);
const pickVersion = (rows) => {
  // 삭제 대상은 후보에서 뺀다 — 이 조회엔 상태 필터가 없어 DELETE 버전까지 딸려온다.
  const live = (rows ?? []).filter((v) => v.statusCd !== CodeUtil.versionStatusCode.DELETE && !v.delFlg);
  const wip = live.filter((v) => WIP_STATUS.includes(v.statusCd)).sort(byTsDesc("createdTs"));
  if (wip.length) return wip[0];
  const released = live.filter((v) => v.statusCd === CodeUtil.versionStatusCode.RELEASED).sort(byTsDesc("releaseTs"));
  // 릴리즈도 작업중도 없으면(RUNNING/FAILED 등만 있는 경우) 가장 최근 생성분이라도 보여준다.
  return released[0] ?? [...live].sort(byTsDesc("createdTs"))[0] ?? null;
};

/*
  '수요계획 확정' 카드용 — 최신 RELEASED DP 버전.
  대표 버전(pickVersion)과 다르다: 대표는 "지금 작업 중인 것"이고 여기 필요한 건 "확정된 것"이다.
  DP_2026M09_WIP 을 만들고 있어도 확정된 계획은 여전히 DP_2026M08 이다.
*/
const pickReleased = (rows) => (rows ?? [])
  .filter((v) => v.statusCd === CodeUtil.versionStatusCode.RELEASED && !v.delFlg)
  .sort(byTsDesc("releaseTs"))[0] ?? null;

// p13n 저장 키(pageCd) — 홈은 탭이 아니므로 고정 문자열을 쓴다.
const VIEW_NAME = "HOME";

// 레이아웃 모달 라벨용 카드명 키(카드 key → i18n key). 카탈로그 순회로 자동 채움.
const CARD_LABEL_KEY = {
  greeting: "homeCardGreeting", pipeline: "homePipeline", notifications: "homeNotifications", approvals: "homeApprovals",
  aiPrompt: "homeAiPrompt", myTasks: "homeMyTasks", pinned: "homePinned", npi: "homeNpi", safetyStock: "homeSafetyStock",
  meetingAgenda: "homeMeetingAgenda",
  bfAccuracy: "homeBfAccuracy", bfRunStatus: "homeBfRunStatus", bfDeviation: "homeBfDeviation",
  dpPlanLift: "homeDpPlanLift", dpSalesTrend: "homeDpSalesTrend", dpPlanVsActual: "homeDpPlanVsActual", dpVersion: "homeDpVersion",
  ipTargetInv: "homeIpTargetInv", ipInventoryAmt: "homeIpInventoryAmt", ipSafetyStock: "homeIpSafetyStock",
  rpFillRate: "homeRpFillRate", rpOpenExc: "homeRpOpenExc", rpBackorder: "homeRpBackorder",
  mpPlanRate: "homeMpPlanRate", mpExceptions: "homeMpExceptions", mpCapacity: "homeMpCapacity",
};

// 카드별 허용 크기(사전 정의) — {id: "열x행", span, rowSpan, label}. 첫 항목이 기본값(카탈로그 span/rowSpan 과 일치).
//   자유 리사이즈 대신 콘텐츠가 깨지지 않는 크기만 노출(게이지=세로 유지, KPI=1×1/2×1 등). 선택값은 z_p13n layout.size 에 저장.
const SZ = (span, rowSpan, label) => ({ id: `${span}x${rowSpan}`, span, rowSpan, label });
const KPI_SIZES = [SZ(1, 1, "1×1"), SZ(2, 1, "2×1")];
const SIZE_OPTIONS = {
  // 2×2 는 좁은 창 대비 — 폭이 줄면 버전 칩이 한 줄에 하나씩 떨어져 1행(150px)에 안 들어간다.
  //   1행에서도 잘려 사라지지는 않지만(칩 영역이 스크롤된다) 다 보고 싶으면 높이를 키우면 된다.
  greeting: [SZ(2, 1, "2×1"), SZ(4, 1, "4×1"), SZ(2, 2, "2×2")],
  pipeline: [SZ(1, 2, "1×2"), SZ(1, 1, "1×1"), SZ(2, 2, "2×2"), SZ(4, 2, "4×2")],
  notifications: [SZ(2, 1, "2×1"), SZ(1, 1, "1×1"), SZ(2, 2, "2×2")],
  approvals: [SZ(1, 2, "1×2"), SZ(2, 2, "2×2"), SZ(1, 1, "1×1")],
  npi: [SZ(1, 2, "1×2"), SZ(2, 2, "2×2"), SZ(1, 1, "1×1")],
  safetyStock: [SZ(1, 2, "1×2"), SZ(2, 2, "2×2"), SZ(1, 1, "1×1")],
  myTasks: [SZ(1, 2, "1×2"), SZ(1, 1, "1×1"), SZ(2, 1, "2×1"), SZ(1, 4, "1×4")],
  pinned: [SZ(1, 2, "1×2"), SZ(1, 1, "1×1"), SZ(2, 1, "2×1")],
  // 제목 + 회의 시각이 한 줄이라 1열도 견딘다. 배정이 많으면 1×4 로 키워 일주일치를 한눈에.
  meetingAgenda: [SZ(1, 2, "1×2"), SZ(1, 1, "1×1"), SZ(2, 1, "2×1"), SZ(1, 4, "1×4")],
  /*
    대화 뷰를 담아야 하므로 2행이 최소다(1열은 문구가 잘려 애초에 제외).
    예전 1행 옵션(2x1/4x1)을 저장해 둔 사용자는 sizeOf 의 opts.find 가 미스 나면서
    카탈로그 기본(2×2)으로 자동 폴백한다 — 별도 마이그레이션 코드도, 서버 쓰기도 필요 없다.
  */
  aiPrompt: [SZ(2, 2, "2×2"), SZ(4, 2, "4×2")],
  rpFillRate: [SZ(1, 2, "1×2"), SZ(2, 2, "2×2")],
};
const sizeOptionsOf = (key) => SIZE_OPTIONS[key] ?? KPI_SIZES;

const Home = () => {
  const { t, i18n } = useTranslation();
  /*
    tt / ttv 는 이제 i18next 의 t 그대로다. 한때 home* 키가 언어팩에 없어 DEFAULTS(한국어 표)로
    폴백했는데, 6개 언어에 키가 모두 들어가면서 그 표를 지웠다 — 표를 남겨 두면 언어팩과
    갈릴 뿐 화면에는 절대 안 나온다(키가 있으면 언어팩이 이긴다).
    이름을 남긴 건 순전히 배선 때문이다: tt 는 카드 30여 개, ttv 는 HomeAiCard 의 prop 이라
    지금 t 로 바꾸면 상류가 소유한 파일까지 건드리게 된다. 새 코드는 t 를 그대로 쓰면 된다.
  */
  const tt = t;
  const ttv = t;
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [dragKey, setDragKey] = useState(null);   // 드래그 중인 카드 key
  const [overKey, setOverKey] = useState(null);    // 드롭 대상 카드 key

  const userData = (() => {
    try { return JSON.parse(localStorage.getItem("user")) || {}; } catch { return {}; }
  })();
  const roles = userData?.roles ?? [];
  const has = (role) => roles.includes(role);
  // 화면 접근 판정은 사이드바와 같은 규칙 하나로 한다(lv1·lv2·leaf 역할 + 테넌트 제외 목록).
  //   카드 노출(permittedCards)과 버전 칩 이동(openVersionPage)이 같은 판정을 쓴다.
  const { isPermitted } = useMenuAccess();
  const permitted = MODULES.filter((m) => has(m.role));
  const username = userData?.username ?? "";
  const today = new Date().toLocaleDateString(i18n.language);

  // 신문 레이아웃 제호 = 회사명. 벤토에서는 안 쓰지만 모드 전환이 즉시 되게 항상 받아 둔다(단건·캐시성 조회).
  const [company, setCompany] = useState("");
  useEffect(() => {
    companyService.get()
      .then((res) => setCompany(res?.data?.name ?? ""))
      .catch((e) => debug(e));
  }, []);

  // 승인대기 카드 게이팅 — 회사가 접근제어(DP planconfig APPROVAL_ACCESS_CONTROL)를 켠 경우에만 노출.
  const [approvalAcOn, setApprovalAcOn] = useState(false);
  useEffect(() => {
    // planconfig 단건 조회 — APPROVAL_ACCESS_CONTROL 레코드의 boolVal 이 ON 여부(AccessControlAdmin 저장 형태와 동일).
    planConfigService.getConfigCd({ moduleCd: CodeUtil.module.DP, configCd: CodeUtil.dpConfig.APPROVAL_ACCESS_CONTROL, activeFlg: true })
      .then((res) => setApprovalAcOn(Boolean(res?.data?.boolVal)))
      .catch((e) => debug(e));
  }, []);

  /*
    권한 = 하드 게이트: 이 사용자가 볼 수 있는 카드만 카탈로그에서 추린다. (승인대기는 접근제어 ON 시에만)

    두 가지 게이트가 있다.
      · module/role — 계획 모듈 권한(ROLE_APP_*). core 는 항상 통과한다.
      · view        — **그 카드가 여는 화면을 이 사용자가 열 수 있는가**(사이드바와 같은 규칙).
    view 게이트가 필요한 건 core 카드 때문이다: core 는 모듈 권한을 안 보므로,
    대상 화면이 역할로 잠겨 있으면 "보이는데 눌러도 안 열리는 카드" 가 남는다.
    회의 안건이 실제로 그렇다 — 메뉴가 S&OP 아래로 옮겨지면서 그 그룹의 역할 게이트를 물려받았다.
  */
  const permittedCards = CARD_CATALOG.filter(
    (c) => (c.module === "core" || has(c.role)) && (!c.view || isPermitted(c.view)));
  const catalogByKey = Object.fromEntries(permittedCards.map((c) => [c.key, c]));
  // 기본 순서 = 모듈 그룹별로 묶기(공용 → BF → DP → IP → RP → MP). 그룹 안 순서는 카탈로그 그대로.
  const groupedCards = [...permittedCards].sort(
    (a, b) => cardGroupRank(a.module) - cardGroupRank(b.module));
  const defaultLayout = { order: groupedCards.map((c) => c.key), hidden: {}, size: {} };

  // p13n = 소프트 선호: 보이는 카드 중 표시/순서/크기를 사용자별로 개인화(z_p13n layout 키).
  const { layout, loaded: layoutLoaded, loadFailed: layoutFailed, saveLayout } = useDashboardP13n(VIEW_NAME, { defaultLayout });
  const savedOrder = layout?.order?.length ? layout.order : defaultLayout.order;
  // 저장된 순서에 없는(신규/조건부 노출) 권한 카드는 뒤에 보충 — 예: 접근제어 ON 시 나타나는 승인 카드.
  const order = [...savedOrder, ...groupedCards.map((c) => c.key).filter((k) => !savedOrder.includes(k))];
  /*
    카드를 전부 끄는 건 **정상 선택**이다 — 그때는 소개 이미지(HomeEmpty)를 보여 준다.
    ⚠️ 예전엔 전부 hidden 을 과거 저장 버그의 손상으로 보고 전체 카드로 되돌리고 재저장까지 했다.
      그 가드가 있으면 사용자가 의도적으로 다 꺼도 다시 켜진다 — 그래서 없앴다.
  */
  const visibleOrder = order.filter((k) => catalogByKey[k] && !layout?.hidden?.[k]);

  /*
    레이아웃 모드 — "bento"(기본, 4열 격자) | "broadsheet"(신문 지면).
    벤토를 대체하지 않는다: 같은 카드·같은 권한 게이팅을 쓰고 배치만 다르다.
    저장은 기존 layout 객체에 얹는다 — 새 p13n 키를 만들면 순서·크기와 따로 놀 이유가 없다.
    ⚠️ 드래그 스왑과 카드 크기(layout.size)는 벤토 전용이다. 신문 지면은 중요도가 자리를 정하므로
      사용자가 자리를 고르지 않는다 — 모드를 되돌리면 저장된 순서·크기가 그대로 살아 있다.
    ⚠️ **기본 배치를 바꾸는 곳은 카드 설정 모달 한 곳뿐이다.** 툴바 아이콘은 저장하지 않는
      임시 보기다 — 같은 값을 두 곳에서 저장하면 "어느 쪽이 기본이냐"를 사용자가 묻게 된다
      (실제로 그렇게 물어보셨다). 툴바에서 saveLayout 을 부르지 말 것.
  */
  const defaultMode = layout?.mode === "broadsheet" ? "broadsheet" : "bento";
  /*
    임시 보기 — null 이면 기본 배치를 따른다. 새로고침하면 다시 null 이라 기본으로 돌아온다.
    layout 이 늦게 도착해도 null 인 동안은 defaultMode 를 따라가므로 별도 동기화가 필요 없다.
  */
  const [viewMode, setViewMode] = useState(null);
  const mode = viewMode ?? defaultMode;

  /*
    신문 지면의 선택된 면(모듈) — "ALL" | "BF" | "DP" | "IP" | "RP" | "MP".
    모드와 같은 layout 객체에 저장한다: 다음에 홈을 열면 보던 면이 그대로 열린다.
    저장값이 권한 밖 모듈이면 HomeBroadsheet 이 ALL 로 되돌린다(여기서 또 검사하지 않는다).
  */
  const section = layout?.section ?? "ALL";
  const setSection = (next) => saveLayout({ ...(layout ?? defaultLayout), section: next });

  /*
    카드 설정 모달 저장 — 통째로 교체하지 않고 **병합**한다.
    모달이 아는 건 order/hidden/size/mode 뿐이고 section(보고 있던 면) 은 모른다.
    교체하면 그 값이 조용히 사라져, 저장 한 번에 신문 지면이 전체 면으로 되돌아간다.
  */
  const saveCardSettings = (next) => {
    saveLayout({ ...(layout ?? defaultLayout), ...next });
    // 임시 보기를 풀어 방금 정한 기본 배치가 바로 보이게 한다(안 풀면 저장했는데 화면이 그대로다).
    setViewMode(null);
    setLayoutOpen(false);
  };

  // 카드 크기 — 저장된 layout.size[key](허용 목록 중 하나) 우선, 없으면 카탈로그 기본.
  const sizeOf = (key) => {
    const opts = sizeOptionsOf(key);
    const chosen = opts.find((o) => o.id === layout?.size?.[key]);
    if (chosen) return chosen;
    const c = catalogByKey[key];
    return { span: c.span, rowSpan: c.rowSpan ?? 1 };
  };

  /*
    드래그 = 두 카드의 자리 맞바꾸기.
    원래는 리스트처럼 "끼워넣기"(from 을 빼서 to 앞에 삽입)였는데, 여기선 그게 이상하게 보인다 —
    끼워넣으면 두 카드 사이의 카드가 전부 한 칸씩 밀리고, 이 그리드는 카드 크기가 제각각인 데다
    gridAutoFlow:dense 라 밀린 카드들이 앞뒤 빈칸을 찾아 튄다. 사용자는 A 를 B 위에 떨어뜨렸는데
    건드리지도 않은 카드들이 움직이는 걸 보게 된다. 드롭 대상 카드 하나만 강조하는 지금 UI 도
    "이 카드와 바꾼다" 로 읽힌다.
    맞바꾸기는 딱 두 자리만 바뀐다. (설정 모달의 행 드래그는 그대로 끼워넣기 — 거긴 진짜 목록이라 그게 맞다.)
    단, 크기가 다른 두 카드를 바꾸면 그 줄은 어차피 다시 흐른다(2열 카드가 1열 자리로 가면 옆이 밀린다).
  */
  const swapCards = (a, b) => {
    if (!a || !b || a === b) return;
    const arr = [...order];
    const ai = arr.indexOf(a);
    const bi = arr.indexOf(b);
    if (ai < 0 || bi < 0) return;
    arr[ai] = b;
    arr[bi] = a;
    // ⚠️ 통째로 교체하지 않는다 — mode(배치)·section(보던 면)까지 지워진다.
    saveLayout({ ...(layout ?? defaultLayout), order: arr });
  };

  // 인사말 계획버전 칩 — 권한 모듈(DP/IP/RP/MP)별 대표 버전 각각 1개(칩 여러 개). 규칙은 pickVersion 주석 참고.
  const [chipVers, setChipVers] = useState([]);
  const [chipsLoaded, setChipsLoaded] = useState(false);
  // '수요계획 확정' 카드는 같은 응답의 DP 행을 재사용한다 — 같은 걸 두 번 부를 이유가 없다.
  const [dpVer, setDpVer] = useState({ released: null, wip: null });
  const planModuleCds = permitted.map((m) => m.key).filter((k) => ["DP", "IP", "RP", "MP"].includes(k));
  const planModuleKey = planModuleCds.join(",");
  useEffect(() => {
    if (!planModuleKey) { setChipVers([]); setChipsLoaded(true); return; }
    let alive = true;
    // 정렬은 서버에 맡기지 않는다 — 아래 pickVersion 이 상태별로 다른 키를 보기 때문.
    Promise.all(planModuleKey.split(",").map((mod) =>
      planVersionService.getAll({ moduleCd: mod })
        .then((res) => (res?.data?.results ?? []).map((v) => ({ ...v, moduleCd: mod })))
        .catch(() => [])
    )).then((lists) => {
      if (!alive) return;
      // 버전이 없는 모듈도 칩 자리를 남긴다(그냥 사라지면 "왜 IP 가 없지?" 로 헷갈린다).
      const mods = planModuleKey.split(",");
      setChipVers(mods.map((mod, i) => pickVersion(lists[i]) ?? { moduleCd: mod, name: null }));
      const dpRows = lists[mods.indexOf("DP")] ?? [];
      const released = pickReleased(dpRows);
      const rep = pickVersion(dpRows);
      // 작업 중 버전은 대표가 확정본과 다를 때만 있다 — 같으면 진행 중인 게 없다는 뜻이다.
      setDpVer({ released, wip: rep && rep.id !== released?.id ? rep : null });
      setChipsLoaded(true);
    }).catch((e) => { if (alive) { debug(e); setChipVers([]); setChipsLoaded(true); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planModuleKey]);

  // 버전 칩 클릭 → 모듈별 페이지로 버전 프리셋 후 탭 전환(skill §9). 전체 버전 객체를 심는다.
  const dispatch = useDispatch();
  const openVersionPage = (v) => {
    const d = CHIP_DRILL[v?.moduleCd];
    if (!d || !isPermitted(d.viewName)) return;
    dispatch(reduxUtil.removeViewState({ viewName: d.viewName }));
    dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(d.viewName, { [d.key]: v })));
    dispatch(reduxUtil.updateHistoryState(d.viewName));
  };

  // 인사 카드 배지만 앰버 — 다른 카드는 모듈색/중립이지만 인사는 모듈 소속이 아니고,
  //   상단에서 화면을 여는 카드라 따뜻한 톤을 유지한다(모듈색으로 오해될 색은 피함).
  const ctx = { tt, ttv, username, today, permitted, roles, versions: chipVers, versionsLoading: !chipsLoaded, onVersionClick: openVersionPage, approvalAcOn, dpVer };

  /*
    boxSizing: border-box — 이 앱은 <CssBaseline/> 을 안 쓰고 전역 box-sizing 선언도 없어
    브라우저 기본값(content-box)으로 돈다. 그러면 height:100% 에 p:2 의 상하 32px 이 **더해져**
    이 박스가 부모보다 32px 커지고, 그만큼이 창 밖으로 잘린다(스크롤을 끝까지 내려도 남는다).
  */
  return (
    <Box sx={{ height: "100%", boxSizing: "border-box", overflow: "auto", bgcolor: "background.default", p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.25, flexShrink: 0, mb: -0.75 }}>
        {/*
          임시 보기 전환 — **저장하지 않는다.** 기본 배치는 카드 설정 모달에서만 정한다.
          새로고침하면 기본 배치로 돌아온다.

          ⚠️ disableInteractive — MUI Tooltip 은 **기본이 interactive** 라 팝퍼가 포인터 이벤트를
            받는다. 아이콘 바로 아래에 툴팁이 뜨면 커서가 팝퍼에 걸려 버튼에서 mouseleave 가
            나고, 툴팁이 닫히면 다시 버튼 위가 되어 열린다 — 이게 반복되면 화면이 깜빡이고
            버튼이 커서 밑에서 사라져 **오클릭**이 난다. 이 툴바는 아이콘이 26px 간격으로
            붙어 있어 특히 잘 걸린다. 툴팁을 읽기만 하면 되므로 상호작용을 끈다.
        */}
        {LAYOUT_MODE_OPTIONS.map((opt) => {
          const Icon = opt.value === "broadsheet" ? NewspaperIcon : GridViewIcon;
          return (
            <Tooltip key={opt.value} disableInteractive title={tt(opt.labelKey)}>
              <IconButton size="small" onClick={() => setViewMode(opt.value)}
                sx={{ p: 0.4, color: mode === opt.value ? "primary.main" : "text.disabled" }}
                aria-label={tt(opt.labelKey)}>
                <Icon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          );
        })}
        {/* 카드 설정은 두 배치 모두에서 연다 — 표시/숨김과 순서는 신문 지면에도 그대로 적용된다
            (크기만 벤토 전용이라 모달이 신문 모드에서 그 열을 숨긴다). */}
        <Tooltip disableInteractive title={layoutFailed ? t("MSG_NetworkError") : tt("homeLayoutConfig")}>
          <span>
            <IconButton size="small" disabled={layoutFailed} onClick={() => setLayoutOpen(true)}
              sx={{ p: 0.4, ml: 0.5, color: "text.secondary" }} aria-label={tt("homeLayoutConfig")}>
              <TuneIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* 1fr 이 아니라 minmax(0, 1fr) — 1fr 은 minmax(auto, 1fr) 이라 트랙이 카드 콘텐츠의 min-content 밑으로 못 줄어든다.
          그 폭이 상위 flex 로 전파되면 창이 좁을 때 화면 전체에 가로 스크롤이 생긴다. 카드 안 텍스트는 이미 ellipsis 처리돼 있다. */}
      {/* flexShrink: 0 — 이 그리드는 세로 flex 컨테이너의 자식이라 기본값(flex-shrink:1)이면
          부모 높이에 맞춰 **먼저 줄어든다**. 부모가 overflow:auto 여도 줄어든 뒤엔 넘칠 게 없어
          스크롤이 안 생기고, 대신 마지막 행이 잘려 보인다. 자연 높이를 지켜야 부모가 스크롤한다. */}
      {/* HomeSignalProvider — 각 카드가 자기 배지·경고를 신고하고, AI 어시스턴트 카드만 그걸 읽는다.
          ⚠️ Home 본체는 useHomeSignals() 를 부르지 않는다(부르면 신호 하나에 그리드 전체가 재렌더). */}
      {/*
        저장된 layout 이 오기 전에는 카드를 아예 그리지 않는다.
        기본값(벤토)으로 한 번 그렸다가 신문으로 바꾸면 배치가 통째로 번쩍이고,
        그 사이 카드 25장이 전부 리마운트돼 조회도 두 번 돈다. p13n 은 단건이라 금방 온다.
        ⚠️ 여기서 이르게 return 하지 않는다 — 위 툴바(모드 토글)는 계속 보여야 한다.
      */}
      {!layoutLoaded ? <HomeSkeleton /> : visibleOrder.length === 0 ? <HomeEmpty /> : (
      <HomeSignalProvider>
      {mode === "broadsheet" ? (
        /*
          flex:"1 0 auto" — 지면이 **남는 세로를 채우게** 한다.
          신문 단 괘선은 열 박스의 border 라 길이가 그 박스 높이와 같다. 내용 높이에 맡기면
          제일 긴 열에서 선이 뚝 끊기고, 구조를 바꿀 때마다 어느 열이 제일 긴지가 달라져
          같은 증상이 계속 재발한다. 지면이 화면을 채우면 세 열이 다 같은 높이로 늘어나
          괘선이 아래까지 내려간다. 0 축소(shrink 0)라 내용이 더 길면 그대로 넘쳐 스크롤한다.

          moduleColor — 눈썹줄(모듈명)을 벤토 카드 아이콘과 **같은 소스**(ColorUtil.colorSet)로
          칠한다. 앱 규칙이 "색이 곧 권한 소속"이라 한 모듈은 어디서든 한 색이어야 한다.
        */
        <Box sx={{ flex: "1 0 auto", display: "flex", minWidth: 0 }}>
          <HomeBroadsheet
            ctx={ctx}
            company={company}
            cards={visibleOrder.map((k) => catalogByKey[k])}
            section={section}
            onSection={setSection}
            cardLabel={(key) => tt(CARD_LABEL_KEY[key])}
            moduleLabel={(m) => (MODULE_NAME_KEY[m] ? tt(MODULE_NAME_KEY[m]) : null)}
            moduleColor={moduleHex}
            onReorder={swapCards}
            renderCard={(c) => c.render({ ...ctx, moduleKey: c.module, size: sizeOf(c.key), aiStack: true })}
          />
        </Box>
      ) : (
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gridAutoRows: 150, gridAutoFlow: "dense", gap: 1.5, flexShrink: 0 }}>
        {visibleOrder.map((key) => {
          const c = catalogByKey[key];
          const sz = sizeOf(key);
          const isOver = overKey === key && dragKey && dragKey !== key;
          return (
            <Box
              key={key}
              draggable
              onDragStart={(e) => { setDragKey(key); e.dataTransfer.effectAllowed = "move"; }}
              onDragEnd={() => { setDragKey(null); setOverKey(null); }}
              onDragOver={(e) => { e.preventDefault(); if (overKey !== key) setOverKey(key); }}
              onDragLeave={() => { if (overKey === key) setOverKey(null); }}
              onDrop={(e) => { e.preventDefault(); swapCards(dragKey, key); setDragKey(null); setOverKey(null); }}
              sx={{
                gridColumn: `span ${sz.span}`,
                gridRow: `span ${sz.rowSpan}`,
                display: "flex", minHeight: 0,
                cursor: "grab", "&:active": { cursor: "grabbing" },
                opacity: dragKey === key ? 0.4 : 1,
                outline: isOver ? "2px dashed" : "none",
                outlineColor: "primary.main",
                outlineOffset: "-2px",
                borderRadius: 1.5,
                transition: "opacity 0.12s",
              }}
            >
              {c.render({ ...ctx, moduleKey: c.module, size: sz })}
            </Box>
          );
        })}
      </Box>
      )}
      </HomeSignalProvider>
      )}

      <HomeCardModal
        open={layoutOpen}
        onClose={() => setLayoutOpen(false)}
        title={tt("homeLayoutConfig")}
        columnLabel={tt("homeCardColumn")}
        showColumnLabel={tt("homeCardShow")}
        sizeColumnLabel={tt("homeCardSize")}
        modeLabel={tt("homeCardLayout")}
        modeOptions={LAYOUT_MODE_OPTIONS.map((o) => ({ value: o.value, label: tt(o.labelKey) }))}
        regions={CARD_REGIONS.map((r) => ({ id: r.id, label: tt(r.labelKey) }))}
        /*
          section — 목록에서 **어느 카드가 어디 소속인지** 한눈에 보이게 하는 배지 문구.
          25장이 한 줄로 늘어서면 순서를 바꿔도 무엇이 어디로 가는지 알 수 없다.
          모듈 카드는 모듈명, core 는 "공용"(신문 지면의 데스크 레일·마스트헤드로 가는 것들).
        */
        cards={permittedCards.map((c) => ({
          key: c.key,
          label: tt(CARD_LABEL_KEY[c.key]),
          /*
            신문 지면에서 인사말 카드가 차지하는 자리는 인사말이 아니라 **계획 버전 줄**이다.
            같은 스위치가 켜고 끄는 대상이 배치마다 다르므로 이름도 배치를 따라간다 —
            "인사말" 이라 적힌 채 버전 줄을 껐다 켜면 무엇을 만진 건지 알 수 없다.
          */
          ...(c.key === "greeting" ? { modeLabels: { broadsheet: tt("homePlanVersions") } } : {}),
          section: MODULE_NAME_KEY[c.module] ? tt(MODULE_NAME_KEY[c.module]) : tt("homeCardSectionCore"),
          region: regionOf(c.key),
          sizes: sizeOptionsOf(c.key),
          Icon: cardIconOf(c),
          iconColor: c.module === "core" ? coreHex() : moduleHex(c.module),
        }))}
        layout={layout}
        defaultLayout={defaultLayout}
        onSave={saveCardSettings}
      />
    </Box>
  );
};

/*
  v26.10: 카드 홈은 미출시 — 소개 이미지(HomeEmpty)만 보여 준다.
  라우트·이력·사이드바는 그대로 두고 여기서만 갈라야 되돌리기 쉽다(App.js 는 건드리지 않는다).
  Home 본체를 아예 마운트하지 않으므로 카드 조회(p13n·DW·알림 등)도 하나도 나가지 않는다.
  되살릴 때는 HOME_IMAGE_ONLY 를 false 로.
*/
const HOME_IMAGE_ONLY = true;

const HomeEntry = () => (HOME_IMAGE_ONLY
  ? <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}><HomeEmpty /></Box>
  : <Home />);

export default HomeEntry;
